import {makeAutoObservable, reaction, runInAction} from "mobx";
import type {Plan} from "types";
import {isFeatureEnabled, MIN_LENGTH, MIN_CONTINUE_WORDS, FEATURES} from "config";
import {checkError, getNowScannedString, isoToDateTime, limitErrorToTrigger, reprNetworkError} from "utils";
import {logEvent} from "analytics";
import {checkIsSupportedLanguage} from "languageDetection";
import {translate} from "i18n/translator";
import {LocalizedText, resolveText} from "i18n/text";
import type {FluentVariable} from "@fluent/bundle";
import {getReport, scanRequest} from "requests";
import type {AIScanData, GrammarScanData, PlagiarismScanData, ScanResponse} from "requests";
import type {RootStore} from "stores/rootStore";
import {ScanStore} from "./aiScanStore";
import {PlagiarismStore} from "stores/plagiarismStore";
import {GrammarScanStore} from "stores/grammarScanStore";
import {EditorCut, EditorStore, ScanCut} from "stores/editorStore";
import {gtmAppScanStarted, gtmAppScanError, gtmFreeLimitReached, gtmLoginPromptShown, type ScanEntrypoint} from "utils/gtm";
import type {PaywallTrigger, BatchReason, ContinueStep, QuotaReason, IncreaseLimitsProps} from "pages/ScanPage/IncreaseLimitsModal";
import type {SignupReason} from "hooks/useAuth";
import type {PostLoginAction} from "postLoginAction";
import {bestPlanLimit, type PlanRequirement} from "utils/planCoverage";
import type {ScanModeFlags} from "scanParams";
import {countWords, firstNChars, firstNWords} from "utils/text";
import {measurementId} from 'utils/funnel';
import {NO_MODES, modesCoverSection, type FilePrefix} from "stores/batchFileStore";
import {paywallArm, sellsOneTimeScan} from "utils/abTest";
import type {ScanMeasurement} from 'utils/funnel';
import {gtmEvent, gtmInlineCheckout, gtmScanMeasurement} from 'utils/gtm';
import {failureReason, isTransportFailure} from 'stores/toastStore';
import {hasSilencedReplaceText, type InputDraft} from 'persistence';
import {persistLoginState} from 'loginDrafts';

export type ActiveSection = 'AI' | 'Plagiarism' | 'Grammar';
const ALL_SECTIONS: ActiveSection[] = ['AI', 'Plagiarism', 'Grammar'];

/** The four scan modes; 'ai' and 'deep' are the two depths of the AI tab's scan. */
export type ScanModeId = keyof ScanModeFlags;

/** Why a run can't go through on the plan as staged — what the "!" beside "Scan once" explains.
 *  `requests` is an anon out of scans; the rest name the plan limit the run is over. */
export type PlanBlock = 'deep_scan' | 'plagiarism' | 'grammar' | 'chars' | 'words' | 'requests' | 'files';

/** The paywall heading a plan block opens under, when nothing more specific names it. */
const BLOCK_TRIGGER: Record<PlanBlock, PaywallTrigger> = {
    deep_scan: 'deep_scan_limit', plagiarism: 'plagiarism_limit', grammar: 'grammar_limit',
    chars: 'character_limit', words: 'words_limit_reached', requests: 'requests_limit', files: 'document_limit',
};

/** Which card the paywall opens on when it also offers the one-time scan. */
export type OneTimePick = 'one_time' | 'plan';

/** A batch's tripped limits, as the one-time scan's offer names them. Files past the plan's
 *  per-batch cap are what a one-time run pays for (BatchFileStore.oneTimeUuids). */
const BATCH_BLOCK: Record<BatchReason, PlanBlock> = {
    deep_scan: 'deep_scan', plagiarism: 'plagiarism', grammar: 'grammar', file_chars: 'chars', words: 'words',
    files: 'files',
};

/** The section that runs (and shows) a mode. */
export const MODE_SECTION: Record<ScanModeId, ActiveSection> = {deep: 'AI', ai: 'AI', plagiarism: 'Plagiarism', grammar: 'Grammar'};

// Map a paywall trigger to the login modal's context-aware reason, so an anon
// user who hits a limit sees a matching sign-up CTA. Deep-scan / plagiarism /
// certificate walls have no dedicated login state (anons rarely hit them) and
// fall back to the generic prompt.
/** Scan-mode flags, all optional — an override layered over the tab's own run to price one the
 *  user hasn't committed to (basicScanStep, the paywall's free step). The full selection is
 *  ScanModeFlags, the same shape the URL and the login draft carry. */
export type ScanModes = Partial<ScanModeFlags>;

/** The per-count quotas /users/me reports as {used, total}. */
type CountLimitKey = 'deep_scan_requests' | 'grammar_requests' | 'plagiarism_requests';

/** A continue-step before it knows whether it is the last one: `apply` is its effect, and only
 *  the final step also starts the scan. `onApply` is filled in once the chain is complete —
 *  see openScanLimitPaywall. */
type PendingStep = {step: ContinueStep, apply: () => void};
const NOOP = () => {};

/** The wall a spent count quota puts up, by the quota's name (the modal's chip). */
const QUOTA_TRIGGER: Record<QuotaReason, PaywallTrigger> = {
    plagiarism: 'plagiarism_limit', grammar: 'grammar_limit', deep_scan: 'deep_scan_limit',
};

const TRIGGER_TO_SIGNUP_REASON: Record<PaywallTrigger, SignupReason> = {
    // Not the lib's 'chars' copy ("log in to scan up to 10,000 characters at once"): an anon
    // already has that cap, it is the same one the account on offer has. Signing in doesn't
    // lift this wall — only a plan does, and the paywall that sells one comes on the re-run.
    character_limit: 'general',
    rate_limit: 'words',
    daily_limit: 'words',
    requests_limit: 'words',
    words_limit_reached: 'words',
    execution_limit: 'words',
    document_limit: 'document',
    upload_documents: 'document',
    deep_scan_limit: 'general',
    grammar_limit: 'general',
    plagiarism_limit: 'general',
    // Anons never see these triggers' paywall — the Humanize click itself routes
    // them to the sign-in wall with the Humanizer copy.
    humanizer_words: 'humanizer',
    humanizer_quota: 'humanizer',
};

export class InputStore {
    root: RootStore;
    scanAttemptId: string | undefined;
    loginPromptTrigger: string | undefined;

    measurement(): ScanMeasurement {
        const files = this.root.batchFile?.fileUuidMap;
        const count = files?.size ?? 0;
        return {
            scan_attempt_id: this.scanAttemptId,
            input_type: count > 1 ? 'batch' : count ? 'document' : 'text',
            file_count: count,
            ...(count === 1 ? {document_id: Array.from(files!.keys())[0]} : {}),
            ...(count ? {} : {text_chars: this.text.length, word_count: countWords(this.text)}),
        };
    }

    /** The text of the tabs that have not run: one draft, whichever of them it is typed in. */
    private draftText = '';
    /** A tab's own text, from its run until the page is cleared (see ownsText). */
    private ownTexts = new Map<ActiveSection, string>();
    /** The draft's editor. A tab that ran shows its own result's instead. */
    draftEditor: EditorStore;
    /** The error as raised; `error` is its copy in the current language. */
    errorSource: LocalizedText | null | undefined = '';
    errorType: string = '';

    /** The open tab — and the scan Run scan runs: each tab is its own scan type. */
    activeSection: ActiveSection = 'AI';
    /** Sections whose result landed while another tab was open — the dot on the tab. */
    unviewedSections: ActiveSection[] = [];
    /** The AI tab's depth — the deep scan (default) or the simple one. The pick outlives tab
     *  changes; the URL carries it while the AI tab is open (scanModes / useScanModesInUrl). */
    deepScan = true;
    /** Each tab's Extra tools: what its menu shows and what its run adds to the tab's own tool,
     *  on the same text. A run writes its whole set into every tab it fills (see makeRunScanRequest),
     *  so those tabs show the tools that produced their results. The tab's own tool among them
     *  means nothing (see activeExtras). Kept for the session — not in the URL, which names the
     *  tab's own tool only — and gone on a reload. */
    private sectionTools: Record<ActiveSection, ScanModeFlags> = {AI: NO_MODES, Plagiarism: NO_MODES, Grammar: NO_MODES};
    scannedOn: string | undefined = undefined;
    shouldScrollToResults: boolean = false;

    lastRunTime = 0;
    loginPromptCounter: number = 0;
    // Reason forwarded to the login modal when a prompt is triggered; read by
    // the LoginPromptWatcher (GlobalMechanics) when loginPromptCounter changes.
    loginPromptReason: SignupReason = 'general';
    // True when the pending prompt was raised to continue a scan (the limit auth
    // form) — GlobalMechanics forwards it to login() so the post-login restore
    // re-runs the scan. A voluntary login never sets it, so it's only restored.
    loginContinueScan: boolean = false;
    // The continuation of the action that raised the pending prompt: `loginAction` when it
    // can be persisted (a certificate download, which survives the OAuth reload),
    // `loginResume` when it can't (the files an anon dropped). Forwarded to login() beside
    // the two above, so — like them — they belong to the login they are handed to and to no other.
    loginAction: PostLoginAction | null = null;
    loginResume: (() => void) | null = null;
    loginAnalyticsContext: Record<string, unknown> | undefined;

    files: any[] = [];
    fileData: any[] = [];
    file: File | undefined = undefined;

    isHighlightSentences: boolean = true;
    isHighlightWords: boolean = true;

    constructor(root: RootStore) {
        this.root = root;
        this.draftEditor = new EditorStore(root);
        makeAutoObservable(this);
        this.clear();
        // Each section announces its own result; a result behind another tab is flagged
        // until that tab is opened, and the flag goes with the result (a left batch, a cleared page).
        reaction(() => this.aiScanStore.haveResults, ready => this.trackResult('AI', ready));
        reaction(() => this.plagiarismStore.haveResults, ready => this.trackResult('Plagiarism', ready));
        reaction(() => this.grammarScanStore.score !== null, ready => this.trackResult('Grammar', ready));
    }

    private trackResult(section: ActiveSection, ready: boolean) {
        if (!ready) this.clearUnviewed(section);
        else if (section !== this.activeSection && !this.unviewedSections.includes(section)) {
            this.unviewedSections = [...this.unviewedSections, section];
        }
    }

    private clearUnviewed(section: ActiveSection) {
        this.unviewedSections = this.unviewedSections.filter(s => s !== section);
    }

    /** Any of the three scans still running. */
    get isAnyScanLoading(): boolean {
        return this.aiScanStore.isLoading || this.plagiarismStore.isLoading || this.grammarScanStore.isLoading;
    }

    /** The open tab's own scan still running — the Run button's spinner. Each tab runs its
     *  scan on its own: a plagiarism check in flight leaves the AI tab free to start one. */
    get isActiveScanLoading(): boolean {
        return this.sectionStore(this.activeSection).isLoading;
    }

    /** Whether the last run went on one-time scans: its spinner is on "Scan once", not Run scan. */
    private lastRunOneTime = false;

    /** Run scan's spinner: the open tab's scan, unless one-time scans pay for it. */
    get isRunScanLoading(): boolean {
        return !this.lastRunOneTime && this.isActiveScanLoading;
    }

    /** "Scan once" spinner: a purchase on the saved card, or a run on one-time scans with any
     *  of its tools still going. */
    get isOneTimeScanLoading(): boolean {
        return this.oneTimeCharging || (this.lastRunOneTime && this.isAnyScanLoading);
    }

    /** Grammar issues of a text scan: underlined in the input, so the mobile results block
     *  lists only the suggestions. A file scan and a report have no such input and show the
     *  underlined text in the results block itself. */
    get grammarIssuesInInput(): boolean {
        const grammar = this.grammarScanStore;
        return this.activeSection === 'Grammar' && !this.isResultView
            && !grammar.isLoading && grammar.visibleMatches.length > 0;
    }

    /**
     * The character counter's click, in the two states the pill itself shows. Red — the text is
     * past the cap — opens the wall for that overage, sized to the text and offering the
     * truncated run. Otherwise nothing is blocked and the pill is merely a way into the plans:
     * the modal opens with no trigger and no requirement, which titles it as an upgrade and
     * offers the next tier up (pickCoveringPlan degrades to getNextPlan on an empty
     * requirement) — naming a limit here would report a problem the user does not have.
     * An anon is sent to sign up either way — with the cap named only when an account's cap is
     * actually higher than theirs, since the wall promises what signing in brings (today the
     * two caps are equal, so it stays the plain sign-up). A paid account has nothing to be
     * sold under the cap.
     */
    openCharLimitPopup() {
        // While files are staged the counter counts documents, and its wall is the batch's.
        if (this.root.batchFile.isFilesMode && this.openBatchLimitPaywall()) return;
        const userStore = this.root.user;
        const limit = userStore.user?.words_per_generation_limit || 10000;
        const text = this.text;
        const over = text.length > limit;
        if (this.root.widgetAppFlow && over) {
            this.showWidgetCharLimit(text, limit, {continueScan: false});
            return;
        }
        if (userStore.wallsAsAnon) {
            const accountCap = this.root.pricing.PLANS_LIMITS.free?.words_per_generation_limit ?? limit;
            this.promptLogin(over && accountCap > limit ? 'chars' : 'general');
            return;
        }
        if (!over) {
            // The widget's app flow has no plan form for an account under its cap.
            if (userStore.canStartSubscription && !this.root.widgetAppFlow) this.root.modal.showModal("increase-limits-form", {});
            return;
        }
        this.showPaywall({
            trigger: 'character_limit' as PaywallTrigger,
            overage: {textLen: text.length, limit},
            onContinueTruncated: () => this.runScanWithTruncation(text),
            requirement: this.scanRequirement(text),
            onUnblocked: this.retryBlockedRun,
        });
    }

    /**
     * Whether a run has anything to work with at all. This is the one thing
     * `makeRunScanRequest` cannot answer with a modal — a limit has a paywall, an empty batch
     * has nothing to say — so it is both what greys out Run scan and what makes the handle a
     * no-op for the callers that press it on the user's behalf (the landing hand-off, the run
     * resumed after a login). The reason is already on screen either way: the file rows carry
     * their own "won't be scanned" verdict.
     */
    get hasRunnableInput(): boolean {
        const batchFileStore = this.root.batchFile;

        // A report is a record being viewed, not a scan being composed: its editor is
        // frozen and its text is loaded for display, so scanning from here is not offered.
        if (this.root.reportViewMode) {
            return false;
        }
        if (batchFileStore.isFilesMode) {
            // Still uploading (or still under OCR): the document has no extracted text yet.
            if (batchFileStore.isFilesLoading()) return false;
            // Every staged file failed validation — there is nothing a run could send.
            if (!batchFileStore.haveActionableFiles()) return false;
            // Every chip off: a batch with no tool to run.
            if (!batchFileStore.hasAnyMode) return false;
        }
        return true;
    }

    /** The button's own state: nothing to run (hasRunnableInput), this tab's scan already in
     *  flight, or a one-time run still going. A limit is NOT a reason — pressing it is how the
     *  user is told about one. */
    get isRunScanDisabled() {
        if (!this.hasRunnableInput) {
            return true;
        }
        // The button stays enabled while idle — re-scanning the same text is allowed.
        // Only an in-flight scan of this tab disables it.
        if (!this.root.batchFile.isFilesMode && this.isActiveScanLoading) return true;
        // A run on one-time scans holds both buttons until every tool it paid for is back.
        return this.isOneTimeScanLoading;
    }

    /**
     * Why the staged run can't go through on the plan — the first wall it would be refused on,
     * the one the "!" beside "Scan once" names. null when nothing blocks it, or when what blocks
     * it is not a plan limit (a batch over its plan's document cap).
     */
    get planBlock(): PlanBlock | null {
        if (!this.hasRunnableInput) return null;
        if (this.root.batchFile.isFilesMode) {
            const violations = this.batchViolations();
            // An anon's file cap is what their requests pay for (batchFileCap), so over it they are
            // out of requests; a registered plan's is its batch size.
            if (violations.includes('files') && this.root.user.isAnonymous) return 'requests';
            return violations.length ? BATCH_BLOCK[violations[0]] : null;
        }
        const text = this.text;
        if (text.length < MIN_LENGTH) return null;
        if (this.root.user.isAnonymous && (this.remainingRuns() ?? 1) <= 0) return 'requests';
        const [spent] = this.blockedQuotas();
        if (spent) return spent;
        const {tooLongChars, cappedWords} = this.capToCharLimit(text);
        if (tooLongChars) return 'chars';
        return cappedWords > this.affordableWords() ? 'words' : null;
    }

    /** What a plan must offer for the staged run — the plan the "!" tooltip names. */
    get planBlockRequirement(): PlanRequirement {
        return this.root.batchFile.isFilesMode
            ? this.root.batchFile.planRequirement(this.wordCostMultiplier())
            : this.scanRequirement(this.editorText);
    }

    /** Whether one-time scans take the staged run whole: every text within its own cap. */
    get oneTimeFits(): boolean {
        const max = this.root.pricing?.oneTimeScan?.max_chars;
        if (!max) return false;
        const batch = this.root.batchFile;
        return (batch.isFilesMode ? batch.longestOneTimeFileChars() : this.text.length) <= max;
    }

    /** One-time scans the staged run spends: one per text; in a batch, one per file the plan
     *  can't take (see BatchFileStore.oneTimeUuids). */
    get oneTimeNeeded(): number {
        const batch = this.root.batchFile;
        return batch.isFilesMode ? batch.oneTimeUuids().length : 1;
    }

    /** One-time scans bought and not used yet. */
    get oneTimeAvailable(): number {
        return this.root.user.user?.one_time_scans ?? 0;
    }

    /** The scans on hand cover the staged run — "Scan once" then runs it instead of selling it. */
    get hasOneTimeScan(): boolean {
        return this.oneTimeAvailable > 0 && this.oneTimeAvailable >= this.oneTimeNeeded;
    }

    /** The one-time scan can take this run at all: an account (an anon meets the sign-in wall,
     *  never this offer), outside the widget, every text within the one-time cap. */
    private get oneTimeEligible(): boolean {
        return isFeatureEnabled(FEATURES.ONE_TIME_SCAN) && !this.root.widgetMode && !this.root.user.isAnonymous
            && this.oneTimeFits;
    }

    /** "Scan once" in the toolbar, in the arms that sell the one-time scan: only where the plan
     *  refuses the run (the "!" beside it), for an account holding scans (bought, or given back
     *  by a run that failed) or that has bought one before. Run scan keeps its walls. The arm is
     *  asked last: asking enrols. */
    get showsOneTimeButton(): boolean {
        return this.oneTimeEligible && this.hasRunnableInput && !!this.planBlock
            && (this.oneTimeAvailable > 0 || !!this.root.user.user?.one_time_scan_buyer)
            && sellsOneTimeScan(paywallArm());
    }

    /** A batch of several files: the one-time scan is priced, and spent, per file. */
    get oneTimePerFile(): boolean {
        const batch = this.root.batchFile;
        return batch.isFilesMode && batch.fileUuidMap.size > 1;
    }

    /** The plan's wall on this run is known to the user — a paywall told it, or a one-time scan
     *  just went past it — so the "!" need not open on its own until it is gone and back. */
    planBlockExplained = false;

    /** The "!" left the toolbar: the next one is a wall nobody has explained yet. */
    planBlockGone() {
        this.planBlockExplained = false;
    }

    /** The card the next paywall opens on, when it offers the one-time scan (openOneTimeScanPaywall). */
    private oneTimePick: OneTimePick = 'plan';

    /** The one-time scan is sold for this run, beside the plan. Asks the arm, which enrols. */
    get oneTimeOffered(): boolean {
        return this.oneTimeEligible && sellsOneTimeScan(paywallArm());
    }

    /** Every limit wall the scan raises: the paywall of this visitor's arm, with the one-time scan
     *  beside the plan where the arm sells it and the run fits it — the arm alone decides, so
     *  switching the cookie switches the whole feature. Asking the arm enrols, so it is asked
     *  only where a wall really opens. `oneTime: false` is for a wall with nothing left to run:
     *  the one-time scan pays for this run, so it would charge and then scan nothing. */
    showPaywall(props: IncreaseLimitsProps, {oneTime = true}: {oneTime?: boolean} = {}) {
        const offered = oneTime && this.oneTimeOffered;
        if (offered) this.prefetchSavedCards();
        this.planBlockExplained = true;
        this.root.modal.showModal('increase-limits-form', {
            ...props,
            ...(offered ? {
                oneTimeScan: this.oneTimePick,
                oneTimeNeeded: this.oneTimeNeeded,
                onOneTimeScan: this.runOneTimeScan,
            } : {}),
        });
    }

    /**
     * "Scan once" and the links in the "!" tooltip. Covered by the scans on hand, it runs; else the
     * wall this run would meet, opened on the card that was asked for — and with no wall at all,
     * straight to the one-time checkout.
     */
    openOneTimeScanPaywall = (pick: OneTimePick) => {
        this.oneTimePick = pick;
        try {
            const batch = this.root.batchFile.isFilesMode;
            const opened = batch ? this.openBatchLimitPaywall() : this.openScanLimitPaywall(this.editorText);
            if (!opened) {
                this.showPaywall(this.planBlock
                    ? {trigger: BLOCK_TRIGGER[this.planBlock], requirement: this.planBlockRequirement}
                    : {});
            }
        } finally {
            this.oneTimePick = 'plan';
        }
    }

    /** "Scan once" pressed: a run the plan covers goes on the plan and keeps the scans on hand;
     *  else it runs on them, or buys what the run lacks. */
    pressOneTimeScan = () => {
        if (!this.planBlock) this.makeRunScanRequest('run_scan');
        else if (this.hasOneTimeScan) this.runOneTimeScan();
        else void this.buyOneTimeScans();
    }

    /** One-time scans the run needs beyond those on hand — what "Scan once" buys. */
    get oneTimeLacking(): number {
        return Math.max(1, this.oneTimeNeeded - this.oneTimeAvailable);
    }

    /** Buy what the run lacks and run it: through the confirm step, or — where the account chose
     *  to skip it — on the saved card at once, falling back to the step when that doesn't go
     *  through (it shows why, and lets the card be changed). */
    buyOneTimeScans = async () => {
        const user = this.root.user;
        const quantity = this.oneTimeLacking;
        this.prefetchSavedCards();
        if (user.user?.skip_one_time_confirm && user.token) {
            this.oneTimeCharging = true;
            try {
                const {chargeSavedCard} = await import('pages/ScanPage/oneTimeCharge');
                if (await chargeSavedCard(user.token, quantity, {...this.measurement()})) {
                    logEvent(gtmInlineCheckout('GTM_inline_checkout_one_time_scan_purchased', {
                        ...this.measurement(), ...this.root.pricing.oneTimeScanPurchaseParams(quantity),
                        checkout: 'skip_confirm',
                    }, 'scan_once'));
                    await this.afterOneTimePurchase();
                    return;
                }
            } finally {
                runInAction(() => { this.oneTimeCharging = false; });
            }
        }
        this.root.modal.showModal('one-time-confirm', {quantity, onPaid: this.afterOneTimePurchase});
    }

    /** Read the account's saved cards ahead of a one-time checkout: it opens on them rather
     *  than on the card form, which a buyer with a card is never meant to see first. */
    prefetchSavedCards = () => {
        const token = this.root.user.token;
        if (!token || this.root.user.isAnonymous) return;
        import('pages/ScanPage/oneTimeCharge').then(m => m.prefetchCards(token)).catch(() => undefined);
    }

    /** A charge on the saved card in flight, with no form on screen: "Scan once" spins meanwhile. */
    oneTimeCharging = false;

    /** Bought: the scans are on the account (refreshed, so the run finds them), and it runs. */
    afterOneTimePurchase = async () => {
        await this.root.user.refresh();
        this.runOneTimeScan();
    }

    /** Spend the bought scans on the staged run, every selected tool, outside the plan. */
    runOneTimeScan = () => {
        // The buyer just spent a scan on this wall: the "!" has nothing to tell them about it.
        this.planBlockExplained = true;
        return this.makeRunScanRequest('run_scan', {oneTime: true});
    };

    onEdit(html: string, text: string, props={updateHtml: true}) {
        if (this.root.batchFile.isBatchMode) {
            this.clear();
        }
        const editor = this.activeEditor;

        // An edit outdates its own tab's run in flight, and no other: the other tabs ran on
        // texts of their own. A grammar poll can't be aborted, so that run is left to land.
        if (this.ownsText && this.isActiveScanLoading && this.activeSection !== 'Grammar') {
            this.sectionStore(this.activeSection).clear();
        }

        this.setText(text);
        this.setFile(undefined);
        this.setError()
        if (props.updateHtml) {
            editor.onEdited(html);
        }
    }

    setText(text: string) {
        const user = this.root.user.user;
        const textLenLimit = user?.words_per_generation_limit || 10000;
        const wasUnderLimit = this.text.length <= textLenLimit;

        // Log overage transition for funnel analytics; the actual modal / login prompt is deferred to Scan click.
        if (text.length > textLenLimit && wasUnderLimit) {
            logEvent(gtmFreeLimitReached({limit_type: 'max_symbols', limit_value: String(textLenLimit), page_type: 'app_scan', entrypoint: 'text_input_limit'}));
        }

        this.text = text;
        if (!text.trim()) {
            this.clearTab();
        }
    }

    /** The open tab's text: its own once it has run, the shared draft until then. */
    get text(): string {
        return this.ownsText ? this.ownTexts.get(this.activeSection)! : this.draftText;
    }

    set text(text: string) {
        if (this.ownsText) this.ownTexts.set(this.activeSection, text);
        else this.draftText = text;
    }

    /** Whether the open tab has run: its editor is then its own, and nothing typed or wiped in
     *  another tab reaches it. */
    private get ownsText(): boolean {
        return !this.isResultView && this.ownTexts.has(this.activeSection);
    }

    /** A batch and a stored report: one text per page, shown through the result editors (the
     *  selected file, the report's analyses), which they fill themselves. */
    private get isResultView(): boolean {
        return this.root.batchFile.isBatchMode || this.root.reportViewMode;
    }

    /** The tab takes `text` as its own — what its run does, and what loading an analysis into it does. */
    setOwnText(section: ActiveSection, text: string) {
        this.ownTexts.set(section, text);
    }

    /** Every tab a run fills takes its text as its own — the extra tools' tabs too — and the run's
     *  tools with it: switching among them shows the set that produced their results. */
    private claimRunSections(modes: ScanModeFlags) {
        const filled = ALL_SECTIONS.filter(section => section === this.activeSection || modesCoverSection(modes, section));
        filled.forEach(section => this.setOwnText(section, this.text));
        this.sectionTools = {...this.sectionTools, ...Object.fromEntries(filled.map(section => [section, modes]))};
        // The AI tab's own tool is its depth, so a run that filled it sets that too.
        if (modes.ai || modes.deep) this.deepScan = modes.deep;
    }

    /** Every text on the page: the shared draft and each tab's own. */
    get texts(): string[] {
        return [this.draftText, ...this.ownTexts.values()].filter(text => text.length > 0);
    }

    /** Whether any tab holds a text — its own or the shared draft. */
    get hasAnyText(): boolean {
        return this.texts.length > 0;
    }

    /** The open tab's text goes, and a tab that ran takes its own result with it — only its
     *  own. With nothing run anywhere that is the whole page. */
    clearTab() {
        if (!this.ownTexts.size) {
            this.clear();
            return;
        }
        this.text = '';
        this.setError();
        if (this.ownsText) this.sectionStore(this.activeSection).clear();
        else this.draftEditor.clear();
    }

    sectionStore(section: ActiveSection): ScanStore | PlagiarismStore | GrammarScanStore {
        return section === 'Plagiarism' ? this.plagiarismStore
            : section === 'Grammar' ? this.grammarScanStore : this.aiScanStore;
    }

    get plagiarismStore(): PlagiarismStore {
        return this.root.plagiarism;
    }

    get aiScanStore(): ScanStore {
        return this.root.aiScan;
    }

    get grammarScanStore(): GrammarScanStore {
        return this.root.grammar;
    }

    get activeStore(): ScanStore | PlagiarismStore {
        if (this.activeSection === 'Plagiarism') {
            return this.plagiarismStore;
        } else {
            return this.aiScanStore;
        }
    }

    get activeEditor(): EditorStore {
        if (this.ownsText) return this.sectionStore(this.activeSection).editor;
        // A result view's tab with nothing of its own there shows the AI editor.
        if (this.isResultView) {
            return this.activeSection === 'Plagiarism' && this.plagiarismStore.editor.isFilled
                ? this.plagiarismStore.editor : this.aiScanStore.editor;
        }
        return this.draftEditor;
    }

    /** The editor on screen keeps its result's highlighting off: the AI verdict must not paint
     *  under a tab that never produced it (a batch/report tab borrowing the AI editor, see
     *  `activeEditor`), and an anon's result is painted only over their preview. */
    get resultHighlightOff(): boolean {
        const {user, widgetMode, reportViewMode} = this.root;
        const editor = this.activeEditor;
        const borrowedAiEditor = editor === this.aiScanStore.editor && this.activeSection !== 'AI';
        const anon = user.resultsGated || (!user.isReady() && !widgetMode && !reportViewMode);
        return borrowedAiEditor || (anon && !editor.previewShown);
    }

    get currentEditor(): EditorStore {
        if (this.activeSection === 'Plagiarism') {
            return this.plagiarismStore.editor;
        } else {
            return this.aiScanStore.editor;
        }
    }

    // The run the input stands for — the open tab's own scan, or the batch's chips while
    // files are staged (see scanModes); the AI tab in the depth picked.
    get deepScanFlag(): boolean {
        return this.scanModes.deep;
    }

    get aiScanFlag(): boolean {
        return this.scanModes.ai;
    }

    get plagiarismFlag(): boolean {
        return this.scanModes.plagiarism;
    }

    get grammarFlag(): boolean {
        return this.scanModes.grammar;
    }

    /** The AI scan, in either depth. */
    get isAiRun(): boolean {
        return this.scanModes.ai || this.scanModes.deep;
    }

    /** The text the open editor shows — a grammar-checked tab edits its own working text
     *  (accepted fixes land there and nowhere else), every other tab its `text`. */
    get editorText(): string {
        return this.grammarResultShown ? this.grammarScanStore.workingText : this.text;
    }

    /** What the toolbar's copy puts on the clipboard: the editor's text, which for a batch is
     *  the selected file's — the draft is empty there. */
    get shownText(): string {
        const batch = this.root.batchFile;
        if (!batch.isBatchMode) return this.editorText;
        return (batch.selectedFile && batch.filesResults.get(batch.selectedFile)?.text) || '';
    }

    /** The AI tab's depth switch (the segmented control; the widget pins it deep). */
    setDeepScan(deep: boolean) {
        this.deepScan = deep;
    }

    /** The mode the open tab runs — what the mobile menu checks. */
    modeIsOn(mode: ScanModeId): boolean {
        return this.scanModes[mode];
    }

    /**
     * One mode as the whole selection: the pick opens the mode's section — what a tab press
     * does on desktop, and what the mobile menu (which has no tabs) does with each row — and,
     * for the AI pair, sets the depth. An add-on behind a feature flag falls back to the AI tab.
     */
    selectOnlyScan(option: ScanModeId) {
        if (option === 'grammar' && !isFeatureEnabled(FEATURES.GRAMMAR)) option = this.deepScan ? 'deep' : 'ai';
        if (option === 'plagiarism' && !isFeatureEnabled(FEATURES.PLAGIARISM)) option = this.deepScan ? 'deep' : 'ai';
        if (option === 'ai' || option === 'deep') this.deepScan = option === 'deep';
        this.setActiveSection(MODE_SECTION[option]);
    }

    /** The open tab's own tool alone, the AI tab in its depth — what the URL carries. */
    get tabToolModes(): ScanModeFlags {
        const ai = this.activeSection === 'AI';
        return {
            ai: ai && !this.deepScan, deep: ai && this.deepScan,
            plagiarism: this.activeSection === 'Plagiarism', grammar: this.activeSection === 'Grammar',
        };
    }

    /** The single text's run: the open tab's tool (the AI tab in its depth) plus the extra tools. */
    get textModes(): ScanModeFlags {
        const ai = this.activeSection === 'AI';
        const extras = this.activeExtras;
        return {
            ai: ai ? !this.deepScan : extras.ai, deep: ai ? this.deepScan : extras.deep,
            plagiarism: this.activeSection === 'Plagiarism' || extras.plagiarism,
            grammar: this.activeSection === 'Grammar' || extras.grammar,
        };
    }

    /** The open tab's Extra tools, as picked. */
    private get extraTools(): ScanModeFlags {
        return this.sectionTools[this.activeSection];
    }

    private set extraTools(tools: ScanModeFlags) {
        this.sectionTools = {...this.sectionTools, [this.activeSection]: tools};
    }

    /** The extra tools that add to the open tab: its own tool is already the run. */
    get activeExtras(): ScanModeFlags {
        const section = this.activeSection;
        return {
            ai: section !== 'AI' && this.extraTools.ai,
            deep: section !== 'AI' && this.extraTools.deep,
            plagiarism: section !== 'Plagiarism' && this.extraTools.plagiarism,
            grammar: section !== 'Grammar' && this.extraTools.grammar,
        };
    }

    /** How many tools the Extra tools menu adds — the count on its button. */
    get extraToolCount(): number {
        const {ai, deep, plagiarism, grammar} = this.activeExtras;
        return Number(ai || deep) + Number(plagiarism) + Number(grammar);
    }

    /** An extra tool picked for this section's scan, while the tab has no result of its own —
     *  the dot on its tab. */
    extraCoversSection(section: ActiveSection): boolean {
        return section !== this.activeSection && !this.ownTexts.has(section)
            && modesCoverSection(this.activeExtras, section);
    }

    /** A press in the Extra tools menu: the add-ons toggle, an AI depth turns on — or off,
     *  pressed while on; the two depths never both. */
    toggleExtraTool(mode: ScanModeId) {
        const tools = this.extraTools;
        this.extraTools = mode === 'ai' || mode === 'deep'
            ? {...tools, ai: mode === 'ai' && !tools.ai, deep: mode === 'deep' && !tools.deep}
            : {...tools, [mode]: !tools[mode]};
    }

    /** The run as a plain object — what a run would use. Files staged for a batch bring their
     *  own tools (the chips, any number of them); otherwise the tab is the scan, the AI tab in
     *  its depth. The URL and the login draft mirror this — the run, not the tab plus a
     *  remembered depth — so `?plagiarism=true` never rides with a `deepAiScan` that would not run. */
    get scanModes(): ScanModeFlags {
        return this.root.batchFile.isFilesMode ? this.root.batchFile.modes : this.textModes;
    }

    setFile(file?: File) {
        this.file = file;
    }

    /** The Grammar tab is open with a checked text in it — the editor shows the underlined
     *  text, the toolbar its actions, the mobile input area grows to fit. */
    get grammarResultShown(): boolean {
        return this.activeSection === 'Grammar' && this.grammarScanStore.workingText.length > 0;
    }

    setActiveSection(section: ActiveSection) {
        this.clearUnviewed(section);
        if (section === this.activeSection) return;
        this.setError();
        this.activeSection = section;
    }

    setScannedOn(value: string | undefined) {
        this.scannedOn = value;
    }

    /**
     * Trigger scroll to results (call when scan completes)
     */
    triggerScrollToResults() {
        this.shouldScrollToResults = true;
    }

    /**
     * Reset scroll flag after scrolling (call from component after scroll)
     */
    consumeScrollToResults() {
        this.shouldScrollToResults = false;
    }

    castErrorText(error: string, type?: string, args?: Record<string, FluentVariable>) {
        // Try to use error_type__<type> locale string, fallback to original message
        if (type) {
            const error_items = type.split(":");
            const error_code = error_items[error_items.length - 1];
            const localeKey = `error_type__${error_code}`;
            const translated = translate(localeKey, args, error);
            // If translation exists (different from key), use it; otherwise use original error
            error = translated !== localeKey ? translated : error;
        }

        if (checkError(type, "rich_limit") && error) {
            const suffixKey = this.root.user.isFree()
                ? 'scan-error-rich-limit-suffix-free'
                : 'scan-error-rich-limit-suffix-paid';
            return `${error} ${translate(suffixKey)}`.trim();
        }

        return error;
    }

    get error(): string | null | undefined {
        const source = this.errorSource;
        return source ? resolveText(source) : source;
    }

    /** The error as shown to the user.
     *
     *  A scan that failed on our side gives its reserved words back, and saying so
     *  is what stops the "why did my balance drop?" ticket — the counter does dip
     *  while the scan is in flight. Appended here rather than baked into `error` at
     *  `setError` time, because the claim depends on state that is still moving: one
     *  request starts every scan type, so a grammar task that fails fast rejects the
     *  run while the AI half is still polling. At that moment no result is on screen
     *  and the sentence is true; seconds later the AI result lands next to it, having
     *  charged its own words, and a frozen string would go on insisting otherwise.
     *  As a computed it simply stops claiming it. */
    get errorText(): string | null | undefined {
        const error = this.error;
        if (!error || !this.isRefundedScanError(this.errorType) || this.anyScanResultShown) {
            return error;
        }
        // None of the messages this appends to end in a full stop of their own.
        const stem = error.trim().replace(/([^.!?])$/, '$1.');
        return `${stem} ${translate('error-words-not-charged')}`;
    }

    /** @param preTranslated the caller already turned the code into user-facing copy
     *  and composed something around it (the batch upload names the file). Re-running
     *  the code → text lookup here would throw that composition away and show the bare
     *  reason instead, so the message is taken as final — the code is still recorded. */
    setError(error?: any, type: string = "", preTranslated = false) {

        let args: Record<string, FluentVariable> | undefined;
        if (!!error && typeof error === "object") {
            type = error.code;
            args = error.translationArgs;
            error = error.message;
        }
        if (type) {
            logEvent(gtmEvent("GTM_input_error", {error_code: type, page_type: "app_scan", entrypoint: "scan"}));
        }
        const message: LocalizedText | null | undefined = error;

        this.root.setError();
        // Resolved on read, so the banner follows a language switch.
        this.errorSource = message && !preTranslated
            ? () => this.castErrorText(resolveText(message), type, args)
            : message;
        this.errorType = type;
        if (message) {
            console.error("Input error", resolveText(message), type);
        }
    }

    /** The one error code that means "the scan failed on our side and every word it
     *  reserved was released" — `repr_task_error_to_user` reports all terminal task
     *  failures as this. Matched exactly, not by substring: `server:` is merely the
     *  type prefix, so it also covers an upload that reserved nothing
     *  (`server:text_extraction_failed`) and a poll timeout whose scan may still be
     *  running with its words still held. */
    private static readonly REFUNDED_SCAN_ERROR = 'server:service_unavailable';

    private isRefundedScanError(type?: string): boolean {
        return type === InputStore.REFUNDED_SCAN_ERROR;
    }

    /** True when a result of the *same* scan is on screen. Holds settle per scan type
     *  (`SCAN_TYPE_MULTIPLIER`), so an AI+Plagiarism run whose plagiarism half failed
     *  did charge the AI half — claiming nothing was deducted would be a lie. */
    get anyScanResultShown(): boolean {
        const selected = this.root.batchFile.selectedFile;
        const fileState = selected ? this.root.batchFile.filesWaiting.get(selected) : undefined;
        if (fileState) {
            return fileState.scan === 'ready' || fileState.plagiarism === 'ready'
                || fileState.grammar === 'ready';
        }
        return this.aiScanStore.haveResults || this.plagiarismStore.haveResults
            || this.grammarScanStore.score !== null;
    }

    /**
     * Take every scan section out of its loading state. One request starts all of
     * them, so one failure ends all of them — and each section that stays behind
     * spins forever on its tab (grammar did, on `rich_limit:grammar_attempts`).
     * Reset them here, together, rather than store by store at each call site.
     */
    /** Stop the spinners of a run that failed — only of the checks it ran, so another tab's
     *  scan still in flight keeps its own. */
    stopScanLoaders(modes: ScanModes = {ai: true, deep: true, plagiarism: true, grammar: true}) {
        if (modes.ai || modes.deep) this.aiScanStore.setIsLoading(false);
        if (modes.plagiarism) this.plagiarismStore.setIsLoading(false);
        if (modes.grammar) this.grammarScanStore.setLoading(false);
    }

    /**
     * Whether an error code names a plan limit — the backend REFUSED the run rather than failing
     * it. Only POST /scan can produce one: the quotas are permissions on that request and the
     * words are reserved inside it (app: word_holds.py), while the result poll — GET tasks/<id>
     * — carries no quota permissions and reports a failed task as `service_unavailable`.
     *
     * That is the reason the poll handlers (plagiarismStore, the batch waitTask catch) have no
     * paywall branch: a limit code cannot arrive there, and a paywall raised on a misread error
     * would be selling a plan for a service outage.
     */
    isLimitError(code?: string): boolean {
        if (!code) return false;
        return ['rate_limit', 'daily_limit', 'requests_limit', 'words_limit_reached', 'execution_limit_exceeded', 'deepscan_limit', 'plagiarism_attempts', 'grammar_attempts', 'humanizer_words_limit'].some(
            err => checkError(code, err)
        );
    }

    preScanChecks = (text: string, {oneTime = false}: {oneTime?: boolean} = {}): boolean => {
        // if (this.plagiarismFlag && this.root.user.isFree()) {
        //     this.root.modal.showModal("plagiarism-premium-feature");
        //     gtmFreeLimitReached({limit_type: 'deep_scans', limit_value: '0', page_type: 'app_scan', entrypoint: 'plagiarism_premium_check'});
        //     return false;
        // }

        if (text.length < MIN_LENGTH && !this.file) {
            this.setError(() => translate('scan-error-too-short', {min: MIN_LENGTH}), "validation:low_words");
            return false;
        }

        // Anonymous user without the requests this run's tools take — show login form directly,
        // skip the overage paywall. Otherwise they'd see the truncate modal, click Scan, and only
        // then get rejected by the backend with a login prompt. Two modals in a row for the same problem.
        if (!oneTime && this.root.user.wallsAsAnon && (this.remainingRuns() ?? 1) <= 0) {
            this.showAnonLimitCta('requests_limit', {limit_value: String(this.getRemainingRequests() ?? 0), entrypoint: 'pre_scan_remaining_check'});
            return false;
        }

        // A one-time scan has its own cap and no plan to sell past it.
        const oneTimeMax = this.root.pricing?.oneTimeScan?.max_chars ?? 0;
        if (oneTime && text.length > oneTimeMax) {
            this.setError(() => translate('scan-error-one-time-too-long', {limit: oneTimeMax}), "validation:one_time_too_long");
            return false;
        }

        // Overage check must come before language detection: detection is O(n) over the full string,
        // so on a multi-MB paste it would freeze the UI before the overage modal could even appear.
        const textLenLimit = this.root.user.user?.words_per_generation_limit || 10000;
        if (!oneTime && text.length > textLenLimit && !this.file) {
            if (this.root.widgetAppFlow) {
                this.showWidgetCharLimit(text, textLenLimit, {continueScan: true});
                return false;
            }
            // Widget mode delegates modals via postMessage, which strips function props
            // (DataCloneError on the onContinueTruncated callback). Fall back to silent truncation
            // — same behaviour as before this PR — so the widget keeps working.
            if (this.root.widgetMode) {
                this.setTextInPlace(text.slice(0, textLenLimit));
                return this.preScanChecks(this.text);
            }
            // Snapshot text and limit so racey background updates (user.refresh changing the limit,
            // async setText from paste/OCR) cannot diverge from what the modal's CTA promised.
            const snapshotText = text;
            const snapshotLimit = textLenLimit;
            const overageProps = {
                overage: {textLen: snapshotText.length, limit: snapshotLimit},
                onContinueTruncated: () => this.runScanWithTruncation(snapshotText),
                requirement: this.scanRequirement(snapshotText),
                onUnblocked: this.retryBlockedRun,
            };
            // Anons run the long-text gate through showAnonLimitCta — the login form, whose
            // continuation re-runs this very scan and lands them on this same paywall once
            // there is an account to price. Free users get it straight away, with the
            // truncate option; widget handled above.
            if (this.root.user.isAnonymous) {
                this.showAnonLimitCta('character_limit', {limit_value: String(snapshotText.length), entrypoint: 'pre_scan_char_limit'});
            } else {
                this.showPaywall({trigger: 'character_limit', ...overageProps});
            }
            return false;
        }

        if (!checkIsSupportedLanguage(text) && !this.file) {
            this.setError(() => translate('scan-error-unsupported-language'), "validation:unsupported_language");
            return false;
        }

        if (this.root.user.shouldShowEnrollModal()) {
            this.root.modal.showModal("free-premium");
        }

        if (this.root.user.shouldShowFeedbackModal()) {
            // Delay the feedback popup ~2s so a fast backend limit error (which
            // opens the limit paywall) lands first — otherwise the feedback modal
            // flashes and is immediately replaced. Skip it if a modal is already
            // open by then.
            setTimeout(() => {
                if (!this.root.modal.isModalOpen()) {
                    this.root.modal.showModal("feedback-modal");
                }
            }, 2000);
        }
        return true
    }

    /**
     * What a plan must offer for THIS text to scan as selected — the paywall then sells the
     * cheapest plan that covers it instead of the next tier up (see utils/planCoverage).
     *
     * Words are priced in the selected modes (plagiarism ×2, grammar +×1, …) because that is
     * what the backend debits, and counted over the FULL text: a plan whose character cap is
     * short fails on `chars` anyway, so a partial count would price a text no plan would scan.
     *
     * Past every plan's character cap the word count is dropped entirely — no plan can be
     * picked on `chars` regardless, and counting is an O(n) pass that would freeze the click on
     * a multi-MB paste. `knownWords` reuses a count the caller already made over the same text.
     *
     * `modes` prices the run that was actually dispatched instead of the boxes ticked right
     * now — see `dispatchedRun` for why those two can differ.
     */
    private scanRequirement(text: string, knownWords?: number, modes?: ScanModes): PlanRequirement {
        const maxChars = bestPlanLimit(this.root.pricing?.PLANS_LIMITS ?? {}, 'chars');
        if (maxChars > 0 && text.length > maxChars) return {chars: text.length};
        return {chars: text.length, words: (knownWords ?? countWords(text)) * this.wordCostMultiplier(modes)};
    }

    /** The text and scan modes the running scan was dispatched with — what a limit error from
     *  POST /scan refers to. Editing a tab during its scan aborts that tab's result poll (`onEdit`
     *  clears its store), but NOT that request: it is sent without an abort signal, and the mode
     *  checkboxes stay live while it is in flight. So the editor can hold a different run by the
     *  time the 403 lands, and pricing the offer off it would sell against a run nobody made. */
    private dispatchedRun: {text: string, modes: ScanModes} | null = null;

    /** What the rejected run needed, priced exactly as it was sent. */
    private dispatchedRequirement(): PlanRequirement {
        const run = this.dispatchedRun;
        return run ? this.scanRequirement(run.text, undefined, run.modes) : this.scanRequirement(this.text);
    }

    /** Re-run the action a paywall interrupted, once the purchase has landed. */
    private retryBlockedRun = () => this.makeRunScanRequest();

    /** A plan's checkout a link asked for (?upgrade=): an account already on a paid plan has
     *  nothing to buy there, and is told which plan it is on instead. */
    openPlanCheckout = (plan: Plan) => {
        if (this.root.user.canStartSubscription) this.showPaywall({plan}, {oneTime: false});
        else this.root.modal.showModal('current-plan');
    }

    /** The widget's daily limit (`?flow=app`), for a text and a batch alike: an anon signs in on
     *  the host for an account's scans, an account gets the limit form. The host also hears of
     *  it, for its counter. false when it isn't this wall. */
    showWidgetDailyLimit(code?: string): boolean {
        if (!this.root.widgetAppFlow || !checkError(code, 'daily_limit')) return false;
        // Not isAnonymous: that reads false throughout the widget.
        if (this.root.user.user?.is_anon) {
            this.showAnonLimitCta('daily_limit', {limit_value: 'daily_limit', entrypoint: 'widget_daily_limit'});
        } else {
            this.root.modal.showModal('widget-limit', {kind: 'daily', limit: this.root.user.user?.limits?.requests?.today?.total});
        }
        window.parent.postMessage({type: 'daily-limit'}, '*');
        return true;
    }

    /** The widget's text over the cap (`?flow=app`): an anon signs in for an account's bigger
     *  one; an account scans the part the plan takes, or goes Pro in the app. */
    private showWidgetCharLimit(text: string, limit: number, {continueScan}: {continueScan: boolean}) {
        if (this.root.user.user?.is_anon) {
            const accountCap = this.root.pricing.PLANS_LIMITS.free?.words_per_generation_limit ?? limit;
            this.promptLogin(text.length <= accountCap && accountCap > limit ? 'chars' : 'general', {continueScan});
            return;
        }
        this.root.modal.showModal('widget-limit', {
            kind: 'chars', limit, onTruncate: () => this.runScanWithTruncation(text),
        });
    }

    /** Scan the part of `text` the plan takes — its char cap, then its words — and keep the rest
     *  on screen, unscanned. `text` is the snapshot the modal promised on: it wins over the input. */
    runScanWithTruncation = (text: string) => {
        if (this.text !== text) this.setTextInPlace(text);
        const trim = this.trimToFit(text, this.scanModes);
        this.makeRunScanRequest('run_scan', trim?.substantial ? {cut: trim.cut} : {});
    }

    /** The partial result's way to the rest of the text: the wall the full text meets, with no
     *  free option — scanning the same part again is what the user already has. */
    openFullScanPaywall = () => {
        // The partial run itself is still on its way: there is no result to go beyond yet.
        if (this.isActiveScanLoading) return;
        const batch = this.root.batchFile;
        if (batch.isBatchMode) {
            // The run is over and its queue empty: the document goes back in it, so the wall,
            // a purchase's retry and "Scan once" all run it whole like any staged file. It stays
            // listed there if the wall is closed — the user can run or remove it.
            if (batch.selectedFile) batch.restagePartial(batch.selectedFile);
            if (!this.openBatchLimitPaywall({free: false})) this.makeRunScanRequest();
            return;
        }
        if (!this.openScanLimitPaywall(this.editorText, {free: false})) this.makeRunScanRequest();
    }

    /** An account's simple result's way to its locked composition (an anon's is a login): the same
     *  text (a batch's selected file), deep. A spent deep-scan
     *  quota goes straight to its paywall — the basic scan the run's own paywall would offer
     *  instead is the result already on screen. */
    runDeepScanForFullAnalysis = () => {
        if (this.isDeepScanLimitReached()) {
            // No one-time card: it would run the simple scan selected now, not the deep one asked for.
            this.showPaywall({trigger: QUOTA_TRIGGER.deep_scan, spentQuotas: ['deep_scan'], onUnblocked: this.runDeepScanForFullAnalysis},
                {oneTime: false});
            return;
        }
        const batch = this.root.batchFile;
        if (batch.isBatchMode) {
            if (batch.selectedFile) void batch.rescanDeep(batch.selectedFile);
            return;
        }
        this.setDeepScan(true);
        // The composition only: the simple run's extra tools already have their results, and
        // running them again would spend their quotas — or wall on a spent one.
        this.extraTools = NO_MODES;
        this.makeRunScanRequest();
    }

    /** How big the text a partial result left unscanned is, in the unit of the limit that cut it. */
    get fullScanSize(): number {
        const cut = this.activeEditor.cut;
        // A batch has no draft: the document is the cut's own text.
        const text = this.root.batchFile.isBatchMode ? cut?.text ?? '' : this.editorText;
        return cut?.kind === 'words' ? countWords(text) : text.length;
    }

    /** Replace the input with `text` in place — syncs the visible editor too — WITHOUT scanning. */
    setTextInPlace = (text: string) => {
        this.text = text;
        this.activeEditor.setText(text);
    }

    /**
     * Registered single-text scans: when the run can't go through as selected, open the paywall
     * with the free "continue anyway" options and return true (the caller stops). Return false to
     * let the scan proceed.
     *
     * The ORDER of those options is the whole point, because the cheapest fix differs per wall:
     *
     *  1. A spent count quota (plagiarism, grammar checks, deep scans) is absolute — no amount
     *     of trimming buys one back — so it is answered first, by a basic AI scan instead.
     *  2. Trimming answers the rest: the word allowance, or the per-scan character cap that no
     *     mode change fixes.
     *
     * Steps advance in place inside the modal, each retitling it to the wall it clears. Anons get
     * the login form (preScanChecks) and the widget truncates silently, so both skip this.
     */
    openScanLimitPaywall = (text: string, {free = true}: {free?: boolean} = {}): boolean => {
        // Too-short texts never hit a size/word limit — leave them to preScanChecks.
        if (text.length < MIN_LENGTH) return false;

        // The per-scan character cap is unavoidable — no mode change buys more characters — so
        // the word allowance below is judged against the text already capped to it.
        const {charCap, tooLongChars, cappedWords} = this.capToCharLimit(text);
        // The selection as it was before the modal opened. Abandoning the chain half-way puts it
        // back — otherwise the user is left with a silently narrowed selection and no scan to show for it.
        const beforeModes = this.scanModes;
        // --- 1. A spent count quota — answered by the basic scan, which the trim below is then
        //        priced in (its `modes` are what the run ends up in) -------------------------
        const spent = this.blockedQuotas();
        const basic = this.basicScanStep(spent);
        const modes = basic ? basic.modes : this.scanModes;
        const pending: PendingStep[] = basic ? [{step: basic.step, apply: () => this.applyScanModes(modes)}] : [];

        // --- 2. Trim, once the run is priced in the mode it ends up in ----------------------
        const trim = this.trimToFit(text, modes);

        if (!pending.length && !trim) return false; // nothing blocks the scan

        // Is the trimmed prefix still worth scanning? If not there is no free option at all —
        // and that includes the concession queued above, because a chain has to END in a scan.
        // Offering the basic scan that then walks into an untrimmable text would hand the user
        // a dead button, so the whole ladder collapses to the plain paywall. A trim keeps the
        // text whole: the run scans the prefix and shows the rest as unscanned.
        let cut: ScanCut | undefined;
        if (!free || (trim && !trim.substantial)) {
            pending.length = 0;
        } else if (trim) {
            pending.push({step: trim.step, apply: NOOP});
            cut = trim.cut;
        }

        // Every step applies its own effect; only the LAST one also starts the scan. Any step the
        // user can still move past gets the restore, so closing the modal mid-chain leaves no
        // narrowed selection behind.
        const restore = () => this.applyScanModes(beforeModes);
        // The chain ends in a scan of its text on whichever tab it landed. The basic concession
        // opens the AI tab, which takes that text as its own: its old result goes, the draft stays.
        const scanChain = () => {
            if (this.text !== text) {
                this.sectionStore(this.activeSection).clear();
                this.setOwnText(this.activeSection, text);
                this.activeEditor.setText(text);
            }
            this.makeRunScanRequest('run_scan', {cut});
        };
        pending.forEach(({step, apply}, i) => {
            const isLast = i === pending.length - 1;
            step.onApply = isLast ? () => { apply(); scanChain(); } : apply;
            if (!isLast) step.onAbandon = restore;
        });
        const steps = pending.map(p => p.step);

        // With no steps the modal opens on the wall itself; with steps the first one names it.
        const trigger: PaywallTrigger = steps[0]?.trigger ?? trim?.step.trigger
            ?? (spent.length ? QUOTA_TRIGGER[spent[0]] : 'character_limit');
        // Report every problem the run has, not just the one the chain answers first — the modal
        // lists them under one heading and the steps then clear them one at a time, the same
        // contract the batch paywall follows. The word overage is judged against the run as the
        // user CHOSE it (not after the ladder's concession).
        const chosenBudget = this.affordableWords();
        this.showPaywall({
            trigger,
            ...(tooLongChars ? {overage: {textLen: text.length, limit: charCap}} : {}),
            ...(cappedWords > chosenBudget ? {wordsOverage: {words: cappedWords, limit: chosenBudget}} : {}),
            ...(steps.length ? {continueSteps: steps} : {}),
            ...(spent.length ? {spentQuotas: spent} : {}),
            // Priced on the selection the user CHOSE (before the ladder's concessions) — that is
            // the run the paywall promises to unblock.
            // `cappedWords` already counted this text when nothing was cut off.
            requirement: this.scanRequirement(text, tooLongChars ? undefined : cappedWords),
            onUnblocked: this.retryBlockedRun,
        });
        return true;
    }

    /**
     * The free "scan only a prefix" offer for one text — null when the whole thing already fits.
     *
     * The prefix is the largest one within BOTH walls a size limit can raise: the per-scan
     * character cap first (no mode change buys more characters), then the word allowance the run
     * has left in `modes`, judged against the already-capped text so we never shed words that
     * were going to be cut off anyway. `step` names whichever wall did the cutting, so the CTA
     * advertises the number the user is actually getting.
     *
     * `substantial` is false when what survives isn't worth scanning; the caller then has no free
     * option at all. Shared by the single-text ladder and the single-document batch, so the two
     * offer the same prefix on the same terms.
     */
    private trimToFit(text: string, modes: ScanModes): {step: ContinueStep, cut: ScanCut, text: string, substantial: boolean} | null {
        const {charCap, tooLongChars, capped, cappedWords} = this.capToCharLimit(text);
        const wordBudget = this.affordableWords(modes);
        const wordLimited = cappedWords > wordBudget;
        if (!tooLongChars && !wordLimited) return null;

        const trimmed = wordLimited ? firstNWords(capped, wordBudget) : capped;
        const trimmedWords = wordLimited ? wordBudget : cappedWords;
        const wall = wordLimited
            ? {kind: 'words' as const, limit: wordBudget, trigger: 'words_limit_reached' as const}
            : {kind: 'chars' as const, limit: charCap, trigger: 'character_limit' as const};
        return {
            cut: {at: trimmed.length, kind: wall.kind, limit: wall.limit},
            step: {...wall, onApply: NOOP},
            text: trimmed,
            substantial: trimmed.length >= MIN_LENGTH && trimmedWords >= MIN_CONTINUE_WORDS,
        };
    }

    /**
     * The free ladder for a batch of ONE document the plan can't take whole — the same two
     * rungs openScanLimitPaywall walks (spent quota → trim), because with one file "scan the
     * first N files" offers nothing. Only the last step runs the scan;
     * the ones before it advance the modal to the next wall. null ⇒ no free option (no text to
     * cut, nothing worth scanning left, or a cap trimming can't buy back), and the batch falls
     * through to the plain paywall.
     */
    private soleDocumentSteps(): {steps: ContinueStep[], spentQuotas: QuotaReason[]} | null {
        const file = this.root.batchFile.soleFile();
        if (!file?.text) return null;

        // --- 1. A spent count quota ----------------------------------------------------------
        // One document spends one of the tab's count quota, so the binary "quota spent" check is
        // the right reading here — the batch's more-files-than-remain one can't see this file at
        // all when it's over the char limit (oversized files are not in the file count those
        // violations are derived from). It is reported to the modal for the same reason:
        // `violations` can't carry it, and a chain that offers "continue with basic scan" for a
        // wall the modal never named reads as a non-sequitur.
        const spent = this.blockedQuotas();
        const basic = this.basicScanStep(spent);
        const modes = basic ? basic.modes : this.scanModes;
        const steps: ContinueStep[] = basic ? [basic.step] : [];

        // Is one file still allowed at all once the concession is made? A spent scan quota or a
        // zero document cap is not something a shorter text buys back, so offering the prefix
        // would walk the user into a backend limit error instead of a scan.
        if (this.root.batchFile.scannableFileCap(modes) < 1) return null;

        // --- 2. Trim, once the run is priced in the mode it ends up in -----------------------
        const trim = this.trimToFit(file.text, modes);
        if (trim && !trim.substantial) return null; // nothing worth scanning would survive the cut
        if (!trim && !steps.length) return null;    // nothing blocks the run — not ours to answer
        if (trim) steps.push(trim.step);

        // No trim ⇒ the concession alone makes the whole document fit, so it runs it in full;
        // the file is within the char limit in that case, so it survives sendRequest's filter
        // without the prefix exemption.
        const prefix = trim ? {uuid: file.uuid, text: trim.text, cut: trim.cut} : undefined;
        steps[steps.length - 1].onApply = () => this.runBatch(modes, 1, prefix);
        return {steps, spentQuotas: spent};
    }

    /**
     * Start a batch run over the first `count` files, applying the paywall's concessions first
     * (reflected in the scan-mode toggle) so sendRequest picks up the updated flags. Nothing
     * conceded ⇒ don't touch the selection at all: rewriting it with itself would still push
     * every flag back through the feature gates for no reason.
     *
     * `prefix` scans one named file on a prefix of its text instead of the whole document.
     */
    private runBatch(modes: ScanModes, count?: number, prefix?: FilePrefix) {
        if (Object.keys(modes).length) this.applyScanModes(this.effectiveModes(modes));
        // Every flag is read AFTER the concessions land, the ai one included: a grammar-only
        // batch that falls back to a basic scan turns the AI scan ON, and the value captured
        // before the downgrade would still say "no AI scan" — sending a batch that requests no
        // scan type at all.
        this.root.batchFile.sendRequest(this.plagiarismFlag, this.deepScanFlag,
            this.isAiRun, this.root.user.user!, count, this.grammarFlag, prefix);
    }

    /** The count quotas the run would spend that are spent — the single-RUN reading (one text,
     *  or one document, spends one of each). A text runs one scan type, so at most one; a
     *  document runs every tool on the batch's chips. */
    private blockedQuotas(): QuotaReason[] {
        const blocked: QuotaReason[] = [];
        if (this.plagiarismFlag && this.isPlagiarismLimitReached()) blocked.push('plagiarism');
        if (this.grammarFlag && this.isGrammarLimitReached()) blocked.push('grammar');
        if (this.deepScanFlag && this.isDeepScanLimitReached()) blocked.push('deep_scan');
        return blocked;
    }

    /**
     * The free step that answers the spent count quotas, with the run it applies. A spent count
     * quota is absolute: no truncation buys a grammar check back, so the only free answer is to
     * stop spending it — "continue with basic scan": the AI scan at basic depth (the add-on's tab
     * gives way to the AI tab; a deep scan keeps its tab, one step down). A batch's other add-ons
     * still have room and the step never said they would go, so they stay.
     *
     * Single-text and batch share this so the two paywalls cannot drift; each caller decides
     * only WHETHER a quota blocks it (single text: the binary "quota reached"; batch: more files
     * than remain). Everything that offers, prices or applies the step reads `modes` from here,
     * so the file count the paywall advertises is the one the run is charged for.
     */
    private basicScanStep(spent: QuotaReason[]): {step: ContinueStep, modes: ScanModeFlags} | null {
        if (!spent.length) return null;
        return {
            step: {kind: 'basic', trigger: QUOTA_TRIGGER[spent[0]], onApply: NOOP},
            modes: {
                deep: false, ai: true,
                plagiarism: this.plagiarismFlag && !spent.includes('plagiarism'),
                grammar: this.grammarFlag && !spent.includes('grammar'),
            },
        };
    }

    /** The text capped to the per-scan character limit, with the word count of what survives.
     *  That cap is unavoidable — no mode change buys more characters — so it is the basis every
     *  other size judgement works from (see openScanLimitPaywall and trimToFit). */
    private capToCharLimit(text: string): {charCap: number, tooLongChars: boolean, capped: string, cappedWords: number} {
        const charCap = this.root.user.user?.words_per_generation_limit || 10000;
        const tooLongChars = text.length > charCap;
        const capped = tooLongChars ? firstNChars(text, charCap) : text;
        return {charCap, tooLongChars, capped, cappedWords: countWords(capped)};
    }

    /**
     * Restore a whole selection — URL params, the login draft, a paywall concession — as the
     * tab it names and the AI depth it carries. An add-on names its tab (the AI pair only says
     * how deep the AI tab scans); nothing selected, or only an AI depth, is the AI tab.
     */
    applyScanModes(m: ScanModeFlags) {
        // A staged batch owns its selection: the concession lands on its chips, and the tab
        // stays what it is — a view, not the run.
        if (this.root.batchFile.isFilesMode) {
            this.root.batchFile.setModes(m);
            return;
        }
        if (m.ai || m.deep) this.deepScan = m.deep;
        // The AI scan names the tab whenever it runs; the add-ons beside it are extra tools.
        const primary: ScanModeId = m.ai || m.deep ? (this.deepScan ? 'deep' : 'ai')
            : m.plagiarism ? 'plagiarism' : m.grammar ? 'grammar' : this.deepScan ? 'deep' : 'ai';
        this.selectOnlyScan(primary);
        // The primary's own tool is the tab, not an extra one.
        const aiPrimary = primary === 'ai' || primary === 'deep';
        this.extraTools = {
            ai: !aiPrimary && m.ai, deep: !aiPrimary && m.deep,
            plagiarism: primary !== 'plagiarism' && m.plagiarism, grammar: primary !== 'grammar' && m.grammar,
        };
    }

    getRemainingRequests(): number | null {
        const requests = this.root.user.user?.limits?.requests
        const remaining = [requests?.total, requests?.today]
            .filter((b): b is { used: number; total: number } =>
                typeof b?.total === 'number' && typeof b?.used === 'number')
            .map(b => Math.max(0, b.total - b.used));

        return remaining.length ? Math.min(...remaining) : null;
    }

    /** How many runs like this one (a text, or one file of a batch) the request quota still
     *  allows: each tool a run takes spends a request of its own, as the backend counts them.
     *  null when the quota is unknown or unlimited. `modes` as in wordCostMultiplier. */
    remainingRuns(modes: ScanModes = {}): number | null {
        const remaining = this.getRemainingRequests();
        if (remaining === null) return null;
        const run = this.effectiveModes(modes);
        const tools = Number(run.ai || run.deep) + Number(run.plagiarism) + Number(run.grammar);
        return Math.floor(remaining / Math.max(1, tools));
    }

    /**
     * Per-word price of a scan, mirroring the backend's SCAN_TYPE_MULTIPLIER: a plagiarism
     * check bills every word twice, an AI scan (either depth) or a grammar check once, and the
     * tools of one run add up.
     *
     * `modes` overrides the tab's run to price one the user hasn't chosen yet — the downgrade
     * the paywall offers for free (basicScanStep, "continue with basic scan").
     */
    wordCostMultiplier(modes: ScanModes = {}): number {
        const run = this.effectiveModes(modes);
        return Math.max(1, Number(run.ai || run.deep) + (run.plagiarism ? 2 : 0) + Number(run.grammar));
    }

    /** The tab's run with `modes` layered over it — what a run would use. */
    private effectiveModes(modes: ScanModes): ScanModeFlags {
        return {
            ai: modes.ai ?? this.aiScanFlag,
            deep: modes.deep ?? this.deepScanFlag,
            plagiarism: modes.plagiarism ?? this.plagiarismFlag,
            grammar: modes.grammar ?? this.grammarFlag,
        };
    }

    /** How many words of text the remaining allowance still buys in those modes
     *  (allowance ÷ per-word price). */
    affordableWords(modes: ScanModes = {}): number {
        return Math.floor(this.root.user.remainingWords / this.wordCostMultiplier(modes));
    }

    /** A per-count quota from /users/me ({used, total}) only when it's an enforced number
     *  (Free plan); null for unlimited (paid) plans or when the field is absent. */
    private getCountLimit(key: CountLimitKey): {used: number; total: number} | null {
        const q = this.root.user.user?.limits?.[key];
        // An enforced quota always reports both numbers; anything else means "unlimited".
        return q && typeof q.total === 'number' && typeof q.used === 'number'
            ? {used: q.used, total: q.total}
            : null;
    }

    private isCountLimitReached(key: CountLimitKey): boolean {
        const q = this.getCountLimit(key);
        return !!q && q.used >= q.total;
    }

    isDeepScanLimitReached(): boolean {
        return this.isCountLimitReached('deep_scan_requests');
    }

    isGrammarLimitReached(): boolean {
        return this.isCountLimitReached('grammar_requests');
    }

    isPlagiarismLimitReached(): boolean {
        return this.isCountLimitReached('plagiarism_requests');
    }

    /**
     * Remaining deep scans as a quantity, or null when the deep-scan count limit
     * doesn't apply: deep scan not selected, or an unlimited plan
     * (deep_scan_requests.total is null for paid users). A batch consumes one
     * deep scan per file, so unlike the binary single-scan check
     * (isDeepScanLimitReached) the batch path needs the remaining count.
     *
     * `modes` prices an offer the user hasn't taken yet, exactly as wordCostMultiplier
     * does: under the basic-scan downgrade the deep quota stops applying and this returns null.
     */
    remainingDeepScans(modes: ScanModes = {}): number | null {
        if (!this.effectiveModes(modes).deep) return null;
        const ds = this.getCountLimit('deep_scan_requests');
        return ds ? Math.max(0, ds.total - ds.used) : null;
    }

    /** Same for grammar: a batch spends one check per file, so the quota caps how many
     *  files can be scanned. null when it doesn't apply (grammar off — including under
     *  a basic scan that gives it up — or an unlimited plan). */
    remainingGrammarChecks(modes: ScanModes = {}): number | null {
        if (!this.effectiveModes(modes).grammar) return null;
        const g = this.getCountLimit('grammar_requests');
        return g ? Math.max(0, g.total - g.used) : null;
    }

    /** Same for plagiarism: one check per file in a batch, so the quota caps how many files can
     *  be scanned. null when it doesn't apply (plagiarism off — including under a basic scan that
     *  gives it up — or an unlimited plan). */
    remainingPlagiarismChecks(modes: ScanModes = {}): number | null {
        if (!this.effectiveModes(modes).plagiarism) return null;
        const p = this.getCountLimit('plagiarism_requests');
        return p ? Math.max(0, p.total - p.used) : null;
    }

    /**
     * Ask an anon to sign in. `continueScan` re-runs the staged scan afterwards (it rides
     * with the persisted draft, so it survives the OAuth reload); `action` is the same idea
     * for another persisted continuation (a certificate download); `resume` is for one that
     * cannot be serialized — it lives in memory and therefore only survives an in-place login.
     *
     * All three travel to the login through this prompt's state (see GlobalMechanics), which
     * is what keeps them the login's own: one the user walked away from is overwritten here,
     * and a login this prompt never reaches gets none of them.
     */
    promptLogin = (
        reason: SignupReason = 'general',
        {continueScan = false, action = null, resume = null, analyticsContext}: {
            continueScan?: boolean;
            action?: PostLoginAction | null;
            resume?: (() => void) | null;
            analyticsContext?: Record<string, unknown>;
        } = {},
    ) => {
        if (this.root.user.user?.is_anon) {
            logEvent(gtmLoginPromptShown());
            this.loginPromptReason = reason;
            this.loginContinueScan = continueScan;
            this.loginAction = action;
            this.loginResume = resume;
            this.loginAnalyticsContext = analyticsContext;
            if (this.root.widgetMode) {
                // The host runs the login, and its OAuth reloads the page with this frame:
                // the hand-off to it is the last moment the widget can park its state.
                // Blocked or full storage must not cost the user the prompt itself.
                try { persistLoginState(this.root, {autoRun: continueScan, action, resume}); } catch {}
                this.root.modal.showModal("login-prompt", {reason});
            } else {
                this.loginPromptCounter++;
            }
        }
    }

    /**
     * CTA shown when an anonymous user hits a usage limit: always the
     * login/registration prompt, never the paywall. An anon has no plan to upgrade
     * — selling one before there is an account to put it on skips the step every
     * purchase needs anyway — so the wall an anon meets is "sign in first", however
     * many times they meet it.
     *
     * The paywall still comes, just one step later and only if it is really needed:
     * `continueScan` re-runs the scan the moment the account exists, and the
     * registered handler then opens the paywall sized for that very run (see
     * LoginCompleteHandler / init.js restoreInputState).
     *
     * Logs the GTM_free_limit_reached exposure event tagged with the CTA shown, so
     * the funnel stays measurable in GA4.
     */
    showAnonLimitCta = (
        trigger: PaywallTrigger,
        gtm: {limit_value?: string; entrypoint: string},
    ) => {
        logEvent(gtmFreeLimitReached({
            limit_type: 'used_words',
            paywall_trigger: trigger,
            context: {...this.measurement()},
            limit_value: gtm.limit_value,
            page_type: 'app_scan',
            entrypoint: gtm.entrypoint,
            // Widget/iframe limits are the embedding site's funnel, not this one's.
            cta_variant: this.root.widgetMode ? undefined : 'auth',
        }));
        // continueScan: the login was raised BY a scan the user asked for — after it
        // completes we continue that intent and re-run the scan, which is what lands
        // an over-limit account on the paywall with no second click.
        this.loginPromptTrigger = trigger;
        this.promptLogin(TRIGGER_TO_SIGNUP_REASON[trigger] ?? 'general', {continueScan: true});
    }

    notifyScanStarted() {
        this.lastRunTime = Date.now();
    }

    /** The binding "how many staged files can I scan" limit: an anon's is the files the
     *  remaining requests pay for at one per chip (Infinity when unknown → no overage), every
     *  registered plan's is what its batch_doc_upload leaves after the files the batch already ran. */
    private batchFileCap(): number {
        return this.root.user.wallsAsAnon
            ? (this.remainingRuns() ?? Number.POSITIVE_INFINITY)
            : this.root.batchFile.openDocumentSlots;
    }

    /**
     * Every limit the staged batch trips as it stands: total words over the allowance, more
     * files than can be scanned, files over the per-file char limit, and the per-file count
     * quota — each file spends one of the tab's count quota (a deep scan, a grammar check or a
     * plagiarism check), so that quota short of the file count is a limit in its own right, one the user is
     * told about rather than one that silently shrinks the file count the paywall offers.
     *
     * Listed in the order the free steps clear them (words > files > file_chars > plagiarism >
     * grammar > deep_scan), so the paywall's chips read as that order. Empty ⇒ the batch runs
     * as selected.
     */
    private batchViolations(): BatchReason[] {
        const batchFileStore = this.root.batchFile;
        const fileCount = batchFileStore.countScannableFiles();
        const fileCap = this.batchFileCap();
        const deepRemaining = this.remainingDeepScans();
        const grammarRemaining = this.remainingGrammarChecks();
        const plagiarismRemaining = this.remainingPlagiarismChecks();

        const violations: BatchReason[] = [];
        if (batchFileStore.totalScannableWords > this.affordableWords()) violations.push('words');
        if (fileCount > fileCap) violations.push('files');
        // The widget's app flow skips a file over the cap, whose row already says it won't be
        // scanned, and runs the rest: only a batch of nothing else is walled on it.
        if (batchFileStore.charLimitFiles().length > 0 && !(this.root.widgetAppFlow && fileCount > 0)) violations.push('file_chars');
        if (plagiarismRemaining !== null && fileCount > plagiarismRemaining) violations.push('plagiarism');
        if (grammarRemaining !== null && fileCount > grammarRemaining) violations.push('grammar');
        if (deepRemaining !== null && fileCount > deepRemaining) violations.push('deep_scan');
        return violations;
    }

    /**
     * The staged batch's limit wall: every limit it trips, together, with the free steps that
     * clear them and the plan that covers the whole batch (or the signup form for an anon).
     * True when a wall opened — nothing runs; false when the batch is within every limit.
     * Pressed by the run handle before it sends, and by the red documents counter.
     */
    private openBatchLimitPaywall({free = true}: {free?: boolean} = {}): boolean {
        const batchFileStore = this.root.batchFile;
        const violations = this.batchViolations();
        if (violations.length === 0) return false;

        const fileCount = batchFileStore.countScannableFiles();
        // How many document words the allowance still buys in the tab's mode — the remaining
        // words divided by the per-word price (plagiarism ×2), so the chip and the truncation
        // agree with what the backend will actually debit.
        const wordsLimit = this.affordableWords();
        const totalWords = batchFileStore.totalScannableWords;
        const charLimitFiles = batchFileStore.charLimitFiles();
        // Per-file count quotas as batch dimensions of their own: a batch spends one deep
        // scan, one grammar check AND one plagiarism check per file, so a free user can run
        // at most that many files. null when the quota doesn't apply (mode not selected, or
        // unlimited plan) — see remainingDeepScans / remainingGrammarChecks /
        // remainingPlagiarismChecks.
        const deepRemaining = this.remainingDeepScans();
        const grammarRemaining = this.remainingGrammarChecks();
        const plagiarismRemaining = this.remainingPlagiarismChecks();

        // Modal props (overage chips + "Scan N eligible files") for a batch that tripped
        // one or more limits. The files chip measures the whole batch against the plan's
        // batch_doc_upload, so it gets the files the batch already ran too.
        // batchScannableCount() respects every limit at once (file cap, request quota,
        // word allowance) and scannable files already exclude oversized ones, so the same
        // prefix-truncation handles any combination of limits.
        const buildOverageProps = (violations: BatchReason[]) => {
            // The free concession this ladder offers, with the same policy as the single-text
            // paywall (basicScanStep): the spent count quotas are answered by a basic AI scan, and
            // whatever still doesn't fit is truncated afterwards. A batch trips a quota by
            // needing more per file than remains, so `violations` is what "spent" means here.
            const spent = (['plagiarism', 'grammar', 'deep_scan'] as const).filter(q => violations.includes(q));
            const basic = this.basicScanStep(spent);
            const modes = basic ? basic.modes : this.scanModes;

            // How many files a free continue can cover, priced in the modes it will actually
            // run in: the basic scan both frees words and stops spending the per-file quota.
            const scannable = batchFileStore.batchScannableCount(modes);
            // Do the remaining limits still force fewer files than were uploaded?
            const fileTrunc = scannable < fileCount;

            const overage = {
                kind: 'files' as const,
                violations,
                fileCount,
                fileLimit: this.root.user.user?.batch_doc_upload ?? 0,
                ranCount: batchFileStore.planRanCount,
                totalWords,
                wordsLimit,
                scannableCount: scannable,
                deepRemaining: deepRemaining ?? undefined,
                grammarRemaining: grammarRemaining ?? undefined,
                plagiarismRemaining: plagiarismRemaining ?? undefined,
                charLimitCount: charLimitFiles.length,
                charCount: charLimitFiles[0]?.characters_count ?? 0,
                charLimit: this.root.user.user?.words_per_generation_limit ?? 0,
            };

            // The rest of a partly scanned document: the part it already has is no free offer.
            if (!free) return {overage};

            // Nothing left to run, and it is ONE document that doesn't fit: "scan fewer files"
            // is no offer here — the only file IS the batch — so make the single-text offer
            // instead and scan the first N chars / N words of it.
            const sole = scannable <= 0 ? this.soleDocumentSteps() : null;
            if (sole) return {
                overage,
                continueSteps: sole.steps,
                ...(sole.spentQuotas.length ? {spentQuotas: sole.spentQuotas} : {}),
            };

            // A quota is spent → surface the errors one at a time: "Scan first N files" when
            // the remaining limits still truncate, then the basic scan. Only the last
            // step runs the batch; the ones before it just advance the modal to the next wall.
            if (basic) {
                if (scannable <= 0) return {overage}; // nothing runs even after the downgrade — plain paywall
                // The same order as a text's: the tools first, then the part of the input.
                const steps: ContinueStep[] = [basic.step];
                if (fileTrunc) steps.push({kind: 'files', count: scannable, eligible: violations.includes('file_chars'),
                    trigger: 'document_limit', onApply: NOOP});
                steps[steps.length - 1].onApply = () => this.runBatch(modes, scannable);
                return {overage, continueSteps: steps};
            }
            // No spent quota: the existing single-shot "Scan first N files" in the current mode.
            return {
                overage,
                onContinueTruncated: scannable > 0 ? () => this.runBatch(modes, scannable) : undefined,
            };
        };

        // Every plan shares the same combined paywall: a batch can trip several limits at
        // once — total words over the allowance, more files than can be scanned, and/or
        // files over the per-file char limit (banners suppressed on upload), and/or more deep
        // scans / grammar checks than remain. Collect every tripped limit and show them
        // together, in the order the free steps clear them:
        // words > files > file_chars > plagiarism > grammar > deep_scan.
        // The modal offers the cheapest plan that covers the whole staged batch (or, when no
        // plan does, the free steps + sales contact).
        // The only differences: the file cap (see batchFileCap) and the entry point (an anon
        // gets the login form instead, and meets this modal after it — see showAnonLimitCta).
        const isAnon = this.root.user.wallsAsAnon;
        const remaining = isAnon ? this.getRemainingRequests() : null;

        // Sole reason picks its specific GTM trigger; mixed limits fall back to document_limit.
        const sole = violations.length === 1 ? violations[0] : null;
        const trigger: PaywallTrigger =
            // An anon's file cap IS what their requests still pay for (batchFileCap), so 'files'
            // here means "out of scans", never "too many documents for your plan" — and
            // it outranks the other walls because no scan runs at all until it clears.
            // It must be named as the request limit: the auth form this trigger picks
            // reads "your file is too big" under 'document_limit', which is a different
            // limit and, for a 3 kb file, plainly untrue. Same wall the single-text path
            // already reports as requests_limit (preScanChecks).
            isAnon && violations.includes('files') ? 'requests_limit'
            : sole === 'file_chars' ? 'character_limit'
            : sole === 'deep_scan' ? 'deep_scan_limit'
            : sole === 'grammar' ? 'grammar_limit'
            : sole === 'plagiarism' ? 'plagiarism_limit'
            : 'document_limit';
        if (isAnon) {
            this.showAnonLimitCta(trigger, {limit_value: String(remaining ?? ''), entrypoint: 'batch_limit'});
        } else {
            // The widget delegates modals via postMessage; ModalStore.showModal drops
            // the props that can't survive a structured clone (the continue callbacks),
            // so the upgrade CTA still works there.
            this.showPaywall({
                trigger,
                ...buildOverageProps(violations),
                // Sized off the whole staged batch, so the plan on offer covers every
                // file — not just the ones that fit today's limits.
                requirement: batchFileStore.planRequirement(this.wordCostMultiplier()),
                onUnblocked: this.retryBlockedRun,
            });
        }
        return true;
    }

    /**
     * Run the staged text or batch — the single handle every scan goes through: the Run scan
     * button, the scan widget, the run resumed after a login, and the landing hand-off that
     * continues a Scan pressed one origin ago (init.js). It either starts the run or opens the
     * gate that says why it can't — the paywall, or the signup form every anon wall raises — so
     * no caller decides for itself whether a run is possible: one that has an input just presses.
     * (That parallel judgment is what made the hand-off diverge: a file the direct upload marks
     * unscannable was counted as a scannable one, and the arrival got an out-of-scans paywall
     * where uploading the same file by hand greys the button out and says why.)
     *
     * `entrypoint` is what started this run, and it travels with the run's scan_started events:
     * everything the user presses is the default 'run_scan', and only the landing hand-off, which
     * starts a run nobody pressed in the app, passes 'landing_autoscan' (init.js). It is a
     * parameter rather than store state on purpose — a run the paywall defers is resumed by a
     * real click (retryBlockedRun / runBatch), and that click must report itself as one instead
     * of inheriting the hand-off's flag.
     */
    async makeRunScanRequest(entrypoint: ScanEntrypoint = 'run_scan', {oneTime = false, cut}: {oneTime?: boolean, cut?: ScanCut} = {}) {
        // The scan page stays interactive while the app is still loading (see MainPage
        // BlurredScreen), so Run scan can be pressed before the session is ready. Bail
        // until we have a token — otherwise the scan request goes out unauthenticated.
        // Widget mode has its own token lifecycle and never gates on isReady().
        if (!this.root.widgetMode && !this.root.user.isReady()) return;
        // Nothing to run — the state that greys the button out, and the only one this handle
        // answers with silence rather than a modal (see hasRunnableInput).
        if (!this.hasRunnableInput) return;
        // A Restore still loading would land over this run's text and results.
        this.root.history.cancelRestore();
        this.lastRunOneTime = oneTime;
        this.scanAttemptId = measurementId();
        logEvent(gtmScanMeasurement('GTM_app_scan_attempted', {...this.measurement(), entrypoint}));

        // TODO: refactor
        // Is batch mode
        const batchFileStore = this.root.batchFile;

        // The open editor is what gets scanned: a run pressed on a grammar-checked tab starts
        // from its working text, not from the copy the AI editor still shows. A batch scans
        // its files, and the editor then only shows one of them.
        if (!batchFileStore.isFilesMode && this.editorText !== this.text) this.setTextInPlace(this.editorText);

        // The id every request of a one-time run carries — one text, or each file of a batch the
        // plan can't take, each spending a one-time scan of its own. The plan's walls don't apply
        // to those; the rest of the batch runs on the plan alongside them.
        const oneTimeRun = oneTime ? measurementId() : null;

        if (batchFileStore.fileUuidMap.size > 0) {
            if (!oneTimeRun && this.openBatchLimitPaywall()) return;
            this.notifyScanStarted();
            batchFileStore.sendRequest(this.plagiarismFlag, this.deepScanFlag, this.isAiRun, this.root.user.user!, undefined, this.grammarFlag, undefined, entrypoint, oneTimeRun,
                oneTimeRun ? new Set(batchFileStore.oneTimeUuids()) : undefined);
            return;
        }

        this.setError(null);
        // A partial run sends the text up to its cut; the tabs it fills keep the whole text.
        const text = cut ? this.text.slice(0, cut.at) : this.text;

        // Registered single-text scans get one unified free-continue paywall covering the per-scan
        // char cap, the word allowance, and the deep-scan downgrade (see openScanLimitPaywall).
        // Anons get the login form instead (preScanChecks) and the widget truncates silently, so
        // both fall through to the existing paths below.
        // A cut run was already sized to fit by trimToFit; only a spent quota can still stop it (below).
        if (!cut && !oneTimeRun && !this.file && !this.root.widgetMode && !this.root.user.isAnonymous
            && this.openScanLimitPaywall(text)) {
            return;
        }

        if (!this.preScanChecks(text, {oneTime: !!oneTimeRun})) {
            console.log("Exit by scan checks")
            return
        }

        // Count limits for the paths openScanLimitPaywall doesn't cover (anon, widget). Registered
        // users are handled above; the widget can't carry the step callbacks over postMessage, so
        // it just gets the upsell. The tab's scan type is checked BEFORE the run: the backend
        // enforces grammar and plagiarism, so stopping here only saves a round trip that would come
        // back as a 403 — but that 403 arrives mid-scan, which is how "start scanning, then find
        // out plagiarism is gone" happened while deep scans and grammar warned up front.
        const [spent] = oneTimeRun ? [] : this.blockedQuotas();
        if (spent) {
            const trigger = QUOTA_TRIGGER[spent];
            // The last place an anon could still meet the paywall first-hand: a batch spends
            // several checks on one request, so an anon can run out of plagiarism/grammar
            // checks while scans remain. Same wall, same rule — sign in first.
            if (this.root.user.wallsAsAnon) {
                this.showAnonLimitCta(trigger, {limit_value: spent, entrypoint: 'pre_scan_spent_quota'});
                return;
            }
            // A spent count quota has no size — any paid plan lifts it — so no requirement.
            this.showPaywall({trigger, spentQuotas: [spent], onUnblocked: this.retryBlockedRun});
            return;
        }

        // Freeze what this run is, for the limit handlers below (and for plagiarismStore,
        // whose poll outlives the click by a long way). Kept locally too: another tab's run
        // may replace the field before this one fails.
        const run = {text, modes: this.scanModes};
        this.dispatchedRun = run;
        const measurement: ScanMeasurement = {...this.measurement(), text_chars: text.length, word_count: countWords(text)};

        if (this.deepScanFlag) {
            logEvent(gtmAppScanStarted({...measurement, scan_type: 'deep_scan', page_type: 'app_scan', entrypoint}));
        } else if (this.aiScanFlag) {
            logEvent(gtmAppScanStarted({...measurement, scan_type: 'simple_scan', page_type: 'app_scan', entrypoint}));
        }
        if (this.plagiarismFlag) {
            logEvent(gtmAppScanStarted({...measurement, scan_type: 'plagiarism', page_type: 'app_scan', entrypoint}));
        }
        if (this.grammarFlag) {
            logEvent(gtmAppScanStarted({...measurement, scan_type: 'grammar', page_type: 'app_scan', entrypoint}));
        }

        // Each tab keeps its own result: a run replaces only the one it is about to produce,
        // so a deep verdict survives a plagiarism check of the same text.
        this.notifyScanStarted();
        this.claimRunSections(run.modes);
        if (this.isAiRun) {
            const previous = this.aiScanStore.editor.cut;
            this.aiScanStore.clear();
            this.showRunText(this.aiScanStore.editor, previous, cut);
            this.aiScanStore.setScanMode(this.deepScanFlag ? 'deep' : 'simple');
            this.aiScanStore.setIsLoading(true);
        }

        if (this.plagiarismFlag) {
            const previous = this.plagiarismStore.editor.cut;
            this.plagiarismStore.clear();
            this.showRunText(this.plagiarismStore.editor, previous, cut);
            this.plagiarismStore.setIsLoading(true);
        }

        if (this.grammarFlag) {
            this.grammarScanStore.clear();
            this.grammarScanStore.editor.setText(this.text, cut);
            this.grammarScanStore.setLoading(true);
        }

        // Whether POST /scan itself was accepted. Once it is, the words are debited and the
        // per-check tasks are running — a limit error from here on belongs to one check, not
        // to the run, and must not re-run the whole scan (see the catch below).
        let scanAccepted = false;

        try {
            const scanResponse = await scanRequest(
                text,
                null,
                this.isAiRun,
                this.deepScanFlag,
                this.plagiarismFlag,
                this.grammarFlag,
                "app",
                undefined,
                null,
                oneTimeRun,
            );
            scanAccepted = true;
            Object.assign(measurement, {analysis_id: scanResponse.analysis_id, analysis_uuid: scanResponse.analysis_uuid});
            this.root.certificate?.rememberAnalysis(measurement);
            logEvent(gtmScanMeasurement('GTM_app_scan_accepted', {...measurement, cached: scanResponse.existing}));
            // The analysis belongs to the result it produced — the certificate and the
            // feedback of a tab are those of its own run.
            if (this.isAiRun) {
                this.aiScanStore.setAnalysis(scanResponse.analysis_id, scanResponse.analysis_uuid);
            }
            if (this.plagiarismFlag) {
                this.plagiarismStore.setAnalysisUuid(scanResponse.analysis_uuid);
            }

            // Words and the per-count quotas are debited synchronously by POST /scan, so the
            // sidebar counter can be corrected the moment the request returns — no need to wait
            // for the tasks below. Without this a grammar-only scan (fast) or a deep scan (slow)
            // leaves a stale "words left" on screen for as long as the slowest task runs, which
            // is what made the counter look like it only updated on reload. Not awaited — the
            // scan shouldn't wait on it (userStore.refresh swallows its own errors).
            void this.root.user.refresh();

            const tasks: Promise<any>[] = [];
            if (scanResponse.ai_task_id) {
                tasks.push(this.aiScanStore.waitAIResult(scanResponse.ai_task_id, measurement));
            }
            if ((scanResponse as any).plagiarism_task_id) {
                tasks.push(this.plagiarismStore.waitPlagiarismResult(scanResponse.plagiarism_task_id, measurement));
            }
            if (isFeatureEnabled(FEATURES.GRAMMAR) && (scanResponse as any).grammar_task_id) {
                tasks.push(this.grammarScanStore.waitGrammarResult((scanResponse as any).grammar_task_id, measurement));
            }
            await Promise.all(tasks);

            this.setScannedOn(getNowScannedString());
            this.root.announcement.scanLanded();
            // Grammar scan: the input already fills the screen with the underlined text —
            // jumping to the results block would scroll it away.
            if (!this.grammarFlag) this.triggerScrollToResults();

            this.root.history.page = 1;
            await this.root.refresh();
        } catch (e) {
            this.stopScanLoaders(run.modes);
            // A tab the run left without a result has no scanned part to mark off. Only the tabs
            // this run filled: another may hold a partial run of its own still in flight.
            const {ai, deep, plagiarism, grammar} = run.modes;
            if ((ai || deep) && !this.aiScanStore.haveResults) this.aiScanStore.editor.dropCut();
            if (plagiarism && !this.plagiarismStore.haveResults) this.plagiarismStore.editor.dropCut();
            if (grammar && this.grammarScanStore.score === null) this.grammarScanStore.editor.dropCut();
            console.error(e);
            await this.root.refresh();

            const errorInfo = reprNetworkError(e);
            // A one-time run that failed entirely gave the purchase back (the backend hands it
            // back once every task has failed): say so, or the returning button reads as a glitch.
            if (oneTimeRun && this.hasOneTimeScan) {
                this.setError(() => translate('scan-error-one-time-returned'), errorInfo.code || '', true);
                return;
            }
            // A task that failed on its own names itself (see grammarScanStore); only
            // fall back to the flags when the failure came from the scan request itself.
            const failedCheck = (e as any)?.scanKind as string | undefined;

            const scanType = failedCheck
                ? failedCheck
                : this.deepScanFlag ? 'deep_scan' : this.aiScanFlag ? 'simple_scan' : this.plagiarismFlag ? 'plagiarism' : 'grammar';
            logEvent(gtmAppScanError({...measurement, scan_type: scanType, error_code: errorInfo.code || 'unknown', error_message: errorInfo.message || '', error_stage: scanAccepted ? 'result_poll' : 'scan_request', page_type: 'app_scan', entrypoint: 'make_run_scan'}));

            // The backend rejected THIS run, so size the offer off it — otherwise the 403 path
            // sells a tier that would reject it again. From the dispatch snapshot rather than the
            // editor, which can have moved on by now (see `dispatchedRun`).
            //
            // `onUnblocked` re-runs the scan, so it is offered only when POST /scan itself was
            // refused: once that request is accepted the words are reserved and the checks are
            // running, and re-running would pay for them twice. Quotas are permissions on that
            // one request — the result polls report a failed task as `service_unavailable` — so
            // the flag currently states an invariant rather than picking between two live cases,
            // and it is what keeps "unblock" meaning "re-run the run that was refused".
            const limitProps = {
                requirement: this.dispatchedRequirement(),
                onUnblocked: scanAccepted ? undefined : this.retryBlockedRun,
            };
            if (this.showWidgetDailyLimit(errorInfo.code)) return;
            // NOT isAnonymous (for widget mode too)
            if (this.root.user.user?.is_anon && this.isLimitError(errorInfo.code)) {
                // An anon meets the login form; the scan is re-run once they are signed in, and
                // it is that run — priced on a real account — that opens the paywall below.
                this.showAnonLimitCta(
                    limitErrorToTrigger(errorInfo.code),
                    {limit_value: errorInfo.code || '', entrypoint: 'scan_limit_error'},
                );
            } else if (this.isLimitError(errorInfo.code)) {
                // Any registered user — Free OR paid — hitting a plan limit gets the upgrade
                // paywall; the modal resolves the cheapest plan that covers this run (and the
                // sales contact when none does). Previously only Free reached the modal and paid
                // users fell through to an inline "Word limit reached" banner with no action.
                this.showPaywall({
                    trigger: limitErrorToTrigger(errorInfo.code),
                    ...limitProps,
                });
            } else if (failedCheck === 'grammar') {
                // Name the check that failed, the way plagiarism already does — a bare
                // network message next to a filled-in AI result reads as unrelated.
                // Same order as the batch upload: resolve the code, then wrap it, and
                // mark the result final so the code lookup doesn't undo the wrapping.
                const message = () => translate('scan-error-grammar', {
                    details: this.castErrorText(errorInfo.message, errorInfo.code, errorInfo.translationArgs),
                });
                this.setError({...errorInfo, message}, "", true);
            } else if (isTransportFailure(e)) {
                // Not "network" for every failure with nothing to explain it: a server
                // error says it was ours.
                this.setError({...errorInfo, message: failureReason(e)}, "", true);
            } else {
                this.setError(errorInfo);
            }
        }
    }

    /** A tab's editor as its run starts: cut where a partial run stops, or — when the full text
     *  follows a partial result — with the divider still where that one stopped. */
    private showRunText(editor: EditorStore, previous: EditorCut | null, cut?: ScanCut) {
        const resumes = !cut && previous && this.text.length > previous.at
            && this.text.startsWith(previous.text.slice(0, previous.at));
        if (resumes) editor.setText(this.text, previous, {scanning: true});
        else editor.setText(this.text, cut);
    }

    /** Whether a stored analysis of `text` may go on the page: at once when the tabs it fills
     *  would lose nothing by it, else on the answer to `Replace current text?` (`body`, a locale
     *  id, says where the analysis comes from; the window names the tab it opens on). */
    confirmReplace(text: string, body: string, sections: ActiveSection[]): Promise<boolean> {
        const {batchFile, modal} = this.root;
        // "Don't ask again" is an answer about a text. A batch is a different loss — the open
        // drops the files and every other check's result — so that confirm keeps asking.
        const replacesBatch = batchFile.isBatchMode || batchFile.isFilesMode;
        const loses = (own: string) => !!own.trim() && own.trim() !== text.trim();
        // A filled tab without a text of its own shows the draft, which goes in any case.
        const losing = sections.find(section => loses(this.ownTexts.get(section) ?? this.draftText));
        const replacesText = !!losing || loses(this.draftText);
        if (!replacesBatch && (!replacesText || hasSilencedReplaceText())) return Promise.resolve(true);
        return new Promise(resolve => modal.showModal('replace-text', {
            body, section: losing ?? sections[0], onReplace: () => resolve(true), onDismiss: () => resolve(false),
        }));
    }

    /** The tabs a stored analysis fills, the one it opens on first: the AI verdict's, if any.
     *  An analysis with no check this build shows lands on the AI tab as a text. */
    analysisSections(items: Pick<ScanResponse, 'type'>[]): ActiveSection[] {
        const types = new Set(items.map(item => item.type));
        const sections = ALL_SECTIONS.filter(section =>
            section === 'AI' ? types.has('ai')
            : section === 'Plagiarism' ? types.has('plagiarism') && isFeatureEnabled(FEATURES.PLAGIARISM)
            : types.has('grammar') && isFeatureEnabled(FEATURES.GRAMMAR));
        return sections.length ? sections : ['AI'];
    }

    /** A stored analysis on the page as its run left it: each finished check's result in its
     *  own tab, live to edit and run again (the report page shows the same, frozen), and the
     *  text as the draft of the tabs without one. Opens the AI tab when there is a verdict,
     *  else the first tab with a result. The other tabs keep a text and result of their own,
     *  unless the page held a batch: that goes whole. */
    openAnalysis({uuid, date, text, items}: {uuid: string; date: string; text: string; items: ScanResponse[]}) {
        const sections = this.analysisSections(items);
        this.clear(this.root.batchFile.isActive ? [] : ALL_SECTIONS.filter(section => !sections.includes(section)));
        // Not setText(): that is the scan-input path and fires the char-limit funnel
        // for a text nobody typed. Not `text` either: the open tab may be a kept one.
        this.draftText = text;
        this.draftEditor.setText(text);
        this.setScannedOn(isoToDateTime(date));
        const opened = new Map<ActiveSection, string>();
        for (const item of items) {
            // A check's payload names the text it ran on, as its live poll does; Restore sends
            // the text once, for all, and the stores below read it from the payload.
            const ranOn = item.data.text || text;
            const {type} = item;
            const data = {...item.data, text: ranOn};
            if (type === 'ai') {
                const ai = data as AIScanData;
                const store = this.aiScanStore;
                // The result's depth is the tab's: Run scan again re-runs what is on screen.
                this.setDeepScan(!!ai.deep_scan);
                store.setScanMode(ai.deep_scan ? 'deep' : 'simple');
                store.setAnalysis(null, uuid);
                const mapped = store.mapAIScanSegments(ai.segmentation_data ?? []);
                // A simple scan stores no segments: its text is shown plain.
                if (mapped.length) store.showSegments(mapped);
                else store.editor.setText(ranOn);
                store.setScore(ai.score);
                store.setMetrics(ai);
                store.setScannedText(ranOn);
                opened.set('AI', ranOn);
            } else if (type === 'plagiarism' && isFeatureEnabled(FEATURES.PLAGIARISM)) {
                const plagiarism = data as PlagiarismScanData;
                const store = this.plagiarismStore;
                store.setAnalysisUuid(uuid);
                store.setOriginalText(ranOn);
                store.setPlagiarismData(plagiarism);
                store.editor.setSegments(store.mapPlagiarismSegments(ranOn, plagiarism));
                opened.set('Plagiarism', ranOn);
            } else if (type === 'grammar' && isFeatureEnabled(FEATURES.GRAMMAR)) {
                this.grammarScanStore.editor.setText(ranOn);
                this.grammarScanStore.load(data as GrammarScanData);
                opened.set('Grammar', ranOn);
            }
        }
        opened.forEach((ownText, section) => this.setOwnText(section, ownText));
        this.setActiveSection(sections[0] ?? 'AI');
    }

    /** A login's draft back on the page, with the result it was showing — the one the login
     *  unlocks. It comes back as its public report, after the text; without one the text
     *  alone returns. */
    async restoreDraft({text, analysis}: Pick<InputDraft, 'text' | 'analysis'>) {
        // A login without a reload (the emailed code) never took the page away, and writing
        // the text into the editor again would drop the result's highlighting.
        if (this.text === text) return;
        this.setText(text);
        this.activeEditor.setText(text);
        const section = this.activeSection;
        const report = analysis ? await getReport(analysis).catch(() => null) : null;
        // Typed over, run, switched tabs or a batch up while the report loaded: the page is
        // the user's now, and openAnalysis would replace it.
        if (!report || this.ownTexts.size || this.text !== text || this.activeSection !== section
            || this.root.batchFile.isActive) return;
        // Edited after the scan: the result is no longer this text's.
        if (report.items.some(item => (item.data.text || text).trim() !== text.trim())) return;
        this.openAnalysis({uuid: report.report_id, date: report.date, text, items: report.items});
    }

    /** The page emptied — but for the `keep` tabs' own texts and results. */
    clear(keep: ActiveSection[] = []) {
        this.draftText = '';
        this.draftEditor.clear();
        this.root.batchFile.clearStore();
        for (const section of ALL_SECTIONS) {
            if (keep.includes(section)) continue;
            this.ownTexts.delete(section);
            this.sectionStore(section).clear();
        }
        if (!keep.includes('AI')) this.root.certificate.clear();
        this.errorSource = undefined;
        // The tab stays: a cleared page (or a left batch) is not a reason to change what the
        // next run is — the flows that do want the AI tab (history, Humanizer) open it themselves.
        this.scannedOn = undefined;
        this.shouldScrollToResults = false;

        this.files = [];
        this.fileData = [];

        this.isHighlightSentences = true;
        this.isHighlightWords = true;
    }

    setIsHighlightSentences(value: boolean) {
        this.isHighlightSentences = value;
    }

    setIsHighlightWords(value: boolean) {
        this.isHighlightWords = value;
    }
}
